export class SearchQuery {
    public queryKeywords: any[];
    public queryCityLocations: string[];
    public queryCountyLocations: string[];
    public queryAreas: string[];
    public queryAreaIds: string[];
    public queryTypes: string[];
    public queryPartners: string[];
    public queryString: string;

    private cohortId: number;
    private allowedDecorators: string[];

    private BaseKeywordExpansions: any;
    private c0CityCountyMappings: any;
    private c1CityCountyMappings: any;
    private c2CityCountyMappings: any;
    private c4CityCountyMappings: any;
    private c5CityCountyMappings: any;
    private c7CityCountyMappings: any;
    private c8CityCountyMappings: any;
    private c9CityCountyMappings: any;
    private c10CityCountyMappings: any;

    private c0KeywordExpansions: any;
    private c1KeywordExpansions: any;
    private c2KeywordExpansions: any;
    private c4KeywordExpansions: any;
    private c5KeywordExpansions: any;
    private c7KeywordExpansions: any;
    private c8KeywordExpansions: any;
    private c9KeywordExpansions: any;
    private c10KeywordExpansions: any;

    private debug: boolean;
    private areasHash: any;

    constructor(cohortId: number, queryString: string, debug: boolean = false) {
        // Preserve inputs
        this.cohortId = cohortId;
        this.queryString = queryString;

        this.queryKeywords = [];
        this.queryCityLocations = [];
        this.queryCountyLocations = [];
        this.queryAreas = [];
        this.queryAreaIds = [];
        this.queryTypes = [];
        this.queryPartners = [];

        this.debug = debug;

        // Set default attributes
        this.allowedDecorators = ["location", "area", "type", "partner"];
        this._SetupMappingsAndExpansions();

        // Setup area list
        this.areasHash = {
            bankruptcy: '01',
            'debt-collection': '02',
            contracts: '03',
            'creditor-harrassment': '04',
            'predatory-lending': '05',
            'loans-not-in-collection': '06',
            'public-utilities': '07',
            'deceptive-sales': '08',
            'other-consumer': '09',
            'school-discipline': '12',
            'special-ed': '13',
            'edu-access': '14',
            'edu-vocational': '15',
            'financial-aid': '16',
            'other-edu': '19',
            'wage-claims': '22',
            'earned-income-tax-credit': '23',
            tax: '24',
            'employee-rights': '25',
            'farm-worker-issues': '26',
            adoption: '30',
            custody: '31',
            divorce: '32',
            'adult-guardianship': '33',
            'name-change': '34',
            'term-parental-rights': '35',
            paternity: '36',
            dv: '37',
            'child-support': '38',
            'juv-delinquent': '41',
            'juv-neglected-abused-dependent': '42',
            'juv-emancipation': '43',
            'minor-guardianship': '44',
            medicaid: '51',
            medicare: '52',
            'child-health-insure': '53',
            'community-based-care': '54',
            'private-health-insurance': '55',
            'long-term-health-care': '56',
            'state-and-local-healthcare': '57',
            'fed-subsidized-housing': '61',
            homeownership: '62',
            'landlord-tenant': '63',
            'public-housing': '64',
            'mobile-home': '65',
            'housing-discrimination': '66',
            foreclosures: '67',
            'predatory-mortgage': '68',
            tanf: '71',
            'social-security': '72',
            'food-stamps': '73',
            ssdi: '74',
            ssi: '75',
            'unemployment-comp': '76',
            'vet-benefits': '77',
            'state-and-local-income-maintenance': '78',
            immigration: '81',
            'mental-health': '82',
            'disability-rights': '84',
            'civil-rights': '85',
            'human-trafficking': '86',
            expungement: '87',
            'legal-advice-to-nonprofit-entities': '91',
            'tribal-law': '92',
            'divers-licenses': '93',
            torts: '94',
            'wills-estates': '95',
            'advanced-directives': '96',
            municipal: '97',
            'triabal-criminal': '98'
        }
    }

    /**
     * Processes a string-based search query and decomposes into keyworks, parameters, and locations.
     */
    public PrepareSearchQuery(updatedQueryString: string = null): void {
        try {
            // Parse the search query
            if (updatedQueryString !== null) {
                this.queryString = updatedQueryString;
            }
        } catch (preparationError) {
            console.error(`Failed to decompose search query`, preparationError.toString());

            throw new Error("QueryPreparationError");
        }

        this.queryKeywords = [];
        this.queryCityLocations = [];
        this.queryCountyLocations = [];
        this.queryAreas = [];
        this.queryTypes = [];
        this.queryPartners = [];

        try {
            const tokens = this._tokenizeQuery(this.queryString);

            const classifiedTokens: any =
                this._getParametersAndKeywordsFromTokens(tokens);

            const groupedParameters: any =
                this._groupParameters(classifiedTokens.parameters);

            const locParameters: any =
                this._PrepareLocations(groupedParameters.location, this.cohortId);

            this.queryCityLocations = locParameters.city;
            this.queryCountyLocations = locParameters.county;
            this.queryPartners = groupedParameters.partner;
            this.queryTypes = groupedParameters.type;
            this.queryAreas = groupedParameters.area;
            this.queryAreaIds = this._prepareAreas(groupedParameters.area);
            this.queryKeywords = this._expandKeywords(this.cohortId, classifiedTokens.keywords);
        } catch (preparationError) {
            console.error(`Failed to decompose search query`, preparationError.toString());

            throw new Error("SearchQueryPreparationError");
        }

        return;
    }

    /**
     * Create a human-readable search string based on the query parameters.
     * @param cohortId Take
     * @param wordlist
     */
    public generateSearchQuery(): string {
        let newQueryString: string = "";

        // Add locations (county)
        try {

            for (const county of this.queryCountyLocations) {
                // tslint:disable-next-line:max-line-length
                newQueryString = (newQueryString.length === 0) ? ("location:county:" + county) : (newQueryString + " location:county:" + county);
            }
        } catch (generationError) {
            console.error(`Failed to add locations (county) to search query`, generationError.toString());
        }

        // Add locations (city)
        try {
            for (const city of this.queryCityLocations) {
                newQueryString = (newQueryString.length === 0) ? ("location:city:" + city) : (newQueryString + " location:city:" + city);
            }
        } catch (generationError) {
            console.error(`Failed to add locations (city) to search query`, generationError.toString());
        }

        // Add areas
        try {
            for (const area of this.queryAreas) {
                newQueryString = (newQueryString.length === 0) ? ("area:" + area) : (newQueryString + " area:" + area);
            }
        } catch (generationError) {
            console.error(`Failed to add areas to search query`, generationError.toString());
        }

        // Add types
        try {
            for (const type of this.queryTypes) {
                newQueryString = (newQueryString.length === 0) ? ("type:" + type) : (newQueryString + " type:" + type);
            }
        } catch (generationError) {
            console.error(`Failed to add types to search query`, generationError.toString());
        }

        // Add partners
        try {
            for (const partner of this.queryPartners) {
                if (partner.length >= 2) {
                // tslint:disable-next-line:max-line-length
                    newQueryString = (newQueryString.length === 0) ? ("partner:" + partner) : (newQueryString + " partner:" + partner);
                }
            }
        } catch (generationError) {
            console.error(`Failed to add partners to search query`, generationError.toString());
        }

        // Add keywords
        try {
            for (const keyword of this.queryKeywords) {
                let keyphrase;

                keyphrase = (keyword.base.split(' ').length > 1) ? `"${keyword.base}"` : keyword.base

                newQueryString = (newQueryString.length === 0) ? (keyphrase) : (newQueryString + " " + keyphrase);
            }
        } catch (generationError) {
            console.error(`Failed to add keywords to search query`, generationError.toString());
        }

        this.queryString = newQueryString;

        return newQueryString;
    }

    public async replacePartners(newPartners: string[]): Promise<void> {
        const partnersList: string[] = [];

        for (const partner of newPartners) {
            partnersList.push(partner.trim().substr(0, 10));
        }

        this.queryPartners = partnersList;
    }

    public async replaceTypes(newTypes: string[]): Promise<void> {
        const typesList: string[] = [];

        for (const partner of newTypes) {
            typesList.push(partner.trim().substr(0, 10));
        }

        this.queryTypes = typesList;
    }

    public async replaceAreas(newAreas: string[]): Promise<void> {
        const areasList: string[] = [];

        for (const partner of newAreas) {
            areasList.push(partner.trim().substr(0, 50));
        }

        this.queryAreas = areasList;
    }

    public async replaceLocations(newLocations: any[]): Promise<void> {
        const countyList: string[] = [];
        const cityList: string[] = [];

        for (const location of newLocations) {
            if (location.resolution === "county") {
                countyList.push(location.value);
            } else if (location.resolution === "city") {
                cityList.push(location.value);
            } else {
                console.error(`Unknown resolution "${location.resolution}", ignoring.`);
            }
        }

        this.queryCountyLocations = countyList;
        this.queryCityLocations = cityList;
    }

    public async replaceParameters(
        newPartners: string[],
        newTypes: string[],
        newAreas: string[],
        newLocations: any[],
    ): Promise<void> {
        const promises = [];

        promises.push(this.replacePartners(newPartners));
        promises.push(this.replaceTypes(newTypes));
        promises.push(this.replaceAreas(newAreas));
        promises.push(this.replaceLocations(newLocations));

        Promise.all(promises).then(() => {
            if (this.debug) console.log("Finished updating all parameters.");

            return;
        }).catch((promiseErrors: any) => {
            console.error("Failure updating parameters", promiseErrors);

            throw new Error("ParameterReplacementError");
        });
    }

    /**
     * For a list of words, examine each, and add expansions to a list to be returned.
     * @param cohortId {number} The cohort ID that this search query is for.
     * @param wordlist {string[]} A single array of keywords and expansions.
     */
    private _expandKeywords(cohortId: number, wordlist: string[]) {
        const expandedKeywords: any[] = [];

        if (wordlist.length > 0) {
            for (const word of wordlist) {
                const token: any = {};

                token.base = word;
                token.expansions = [];
                token.expansions = this._lookupTokenExpansions(cohortId, word);

                expandedKeywords.push(token);
            }
        }

        return expandedKeywords;
    }

    /**
     * Breaks the query string into an array of words seperated by space or between quotation marks.
     * @param {String} sSearchQuery The original, unmutated, search string entered by the user.
     * @returns {Promise<Array>} A promise for an array of words/phrases extracted from the search query string.
     */
    private _tokenizeQuery(Query: string) {
        if ((!Query) || (Query.length === 0)) {
            return [];
        }

        if (this.debug) console.log(`Original search query: ${Query}`);

        // Split the string into an array using spaces of quotes as delimeters.
        // ORIGINAL EXPRESSION: const aTokens = sSearchQuery.match(/\w+|"(?:\\"|[^"])+"/g)
        const aTokens = Query.match(/(\w+:\w+:\w+)|(\w+:\w+:"(?:\\"|[^"])+")|(\w+:(\w+(([-]\w+)+)))|(\w+:(\w+))|\w+:"(?:\\"|[^"])+"|"(?:\\"|[^"])+"|(\w+)|#(\w+)/g);

        // Examine each token and remove any lingering quotation marks.
        try {
            for (let i = 0; i < aTokens.length; i += 1) {
                aTokens[i] = aTokens[i].replace(/"/g, "");
            }

            if (this.debug) console.log(`Split query string into ${aTokens.length} tokens.`);

            return aTokens;
        } catch (e) {
            console.error("Unable to iterate over search tokens", aTokens);
            return [];
        }
    }

    /**
     * Configures instance vars with lists of keywork and city expansions.
     */
    private _SetupMappingsAndExpansions() {
        // Expansions used by all cohorts.
        this.BaseKeywordExpansions = {
            benefits: ["ssi", "ssd", "ssdi", "social security", "foodstamps", "veterans", "unemployment"],
            dv: ["injection", "ifp", "protection order", "protective order"],
            dvs: ["injection", "ifp", "protection order", "protective order"],
            family: ["dissolution", "child support", "divorce", "paternity"],
            iep: ["education", "special ed", "special education", "gal", "dependency"],
            kids: ["education", "special ed", "special education", "gal", "dependency", "minors"],
            covid: ["remote", "covid-19", "coronavirus", "eviction", "evictions"],
        };

        // Expansions specifically for Cohort 0
        this.c0CityCountyMappings = {
            "alachua": ["alachua"],
            "alford": ["jackson"],
            "altamonte springs": ["seminole"],
            "altha": ["calhoun"],
            "anna maria": ["manatee"],
            "apalachicola": ["franklin"],
            "apopka": ["orange"],
            "arcadia": ["desoto"],
            "archer": ["alachua"],
            "astatula": ["lake"],
            "atlantic beach": ["duval"],
            "atlantis": ["palm beach"],
            "auburndale": ["polk"],
            "aventura": ["miami-dade"],
            "avon park": ["highlands"],
            "bal harbour": ["miami-dade"],
            "baldwin": ["duval"],
            "bartow": ["polk"],
            "bascom": ["jackson"],
            "bay harbor islands": ["miami-dade"],
            "bay lake": ["orange"],
            "bell": ["gilchrist"],
            "belle glade": ["palm beach"],
            "belle isle": ["orange"],
            "belleair": ["pinellas"],
            "belleair beach": ["pinellas"],
            "belleair bluffs": ["pinellas"],
            "belleair shore": ["pinellas"],
            "belleview": ["marion"],
            "beverly beach": ["flagler"],
            "biscayne park": ["miami-dade"],
            "blountstown": ["calhoun"],
            "boca raton": ["palm beach"],
            "bonifay": ["holmes"],
            "bonita springs": ["lee"],
            "bowling green": ["hardee"],
            "boynton beach": ["palm beach"],
            "bradenton": ["manatee"],
            "bradenton beach": ["manatee"],
            "branford": ["suwannee"],
            "briny breezes": ["palm beach"],
            "bristol": ["liberty"],
            "bronson": ["levy"],
            "brooker": ["bradford"],
            "brooksville": ["hernando"],
            "bunnell": ["flagler"],
            "bushnell": ["sumter"],
            "callahan": ["nassau"],
            "callaway": ["bay"],
            "campbellton": ["jackson"],
            "cape canaveral": ["brevard"],
            "cape coral": ["lee"],
            "carrabelle": ["franklin"],
            "caryville": ["washington"],
            "casselberry": ["seminole"],
            "cedar key": ["levy"],
            "center hill": ["sumter"],
            "century": ["escambia"],
            "chattahoochee": ["gadsden"],
            "chiefland": ["levy"],
            "chipley": ["washington"],
            "cinco bayou": ["okaloosa"],
            "clearwater": ["pinellas"],
            "clermont": ["lake"],
            "clewiston": ["hendry"],
            "cloud lake": ["palm beach"],
            "cocoa": ["brevard"],
            "cocoa beach": ["brevard"],
            "coconut creek": ["broward"],
            "coleman": ["sumter"],
            "cooper city": ["broward"],
            "coral gables": ["miami-dade"],
            "coral springs": ["broward"],
            "cottondale": ["jackson"],
            "crescent city": ["putnam"],
            "crestview": ["okaloosa"],
            "cross city": ["dixie"],
            "crystal river": ["citrus"],
            "cutler bay": ["miami-dade"],
            "dade city": ["pasco"],
            "dania beach": ["broward"],
            "davenport": ["polk"],
            "davie": ["broward"],
            "daytona beach": ["volusia"],
            "daytona beach shores": ["volusia"],
            "debary": ["volusia"],
            "deerfield beach": ["broward"],
            "defuniak springs": ["walton"],
            "deland": ["volusia"],
            "delray beach": ["palm beach"],
            "deltona": ["volusia"],
            "destin": ["okaloosa"],
            "doral": ["miami-dade"],
            "dundee": ["polk"],
            "dunedin": ["pinellas"],
            "dunnellon": ["marion"],
            "eagle lake": ["polk"],
            "eatonville": ["orange"],
            "ebro": ["washington"],
            "edgewater": ["volusia"],
            "edgewood": ["orange"],
            "el portal": ["miami-dade"],
            "esto": ["holmes"],
            "eustis": ["lake"],
            "everglades city": ["collier"],
            "fanning springs": ["gilchrist", "levy"],
            "fellsmere": ["indian river"],
            "fernandina beach": ["nassau"],
            "flagler beach": ["flagler"],
            "florida city": ["miami-dade"],
            "fort lauderdale": ["broward"],
            "fort meade": ["polk"],
            "fort myers": ["lee"],
            "fort myers beach": ["lee"],
            "fort pierce": ["st. lucie"],
            "fort walton beach": ["okaloosa"],
            "fort white": ["columbia"],
            "freeport": ["walton"],
            "frostproof": ["polk"],
            "fruitland park": ["lake"],
            "gainesville": ["alachua"],
            "glen ridge": ["palm beach"],
            "glen st. mary": ["baker"],
            "golden beach": ["miami-dade"],
            "golf": ["palm beach"],
            "graceville": ["jackson"],
            "grand ridge": ["jackson"],
            "grant-valkaria": ["brevard"],
            "green cove springs": ["clay"],
            "greenacres": ["palm beach"],
            "greensboro": ["gadsden"],
            "greenville": ["madison"],
            "greenwood": ["jackson"],
            "gretna": ["gadsden"],
            "groveland": ["lake"],
            "gulf breeze": ["santa rosa"],
            "gulf stream": ["palm beach"],
            "gulfport": ["pinellas"],
            "haines city": ["polk"],
            "hallandale beach": ["broward"],
            "hampton": ["bradford"],
            "hastings": ["st. johns"],
            "havana": ["gadsden"],
            "haverhill": ["palm beach"],
            "hawthorne": ["alachua"],
            "hialeah": ["miami-dade"],
            "hialeah gardens": ["miami-dade"],
            "high springs": ["alachua"],
            "highland beach": ["palm beach"],
            "highland park": ["polk"],
            "hillcrest heights": ["polk"],
            "hilliard": ["nassau"],
            "hillsboro beach": ["broward"],
            "holly hill": ["volusia"],
            "hollywood": ["broward"],
            "holmes beach": ["manatee"],
            "homestead": ["miami-dade"],
            "horseshoe beach": ["dixie"],
            "howey-in-the-hills": ["lake"],
            "hypoluxo": ["palm beach"],
            "indialantic": ["brevard"],
            "indian creek": ["miami-dade"],
            "indian harbour beach": ["brevard"],
            "indian river shores": ["indian river"],
            "indian rocks beach": ["pinellas"],
            "indian shores": ["pinellas"],
            "inglis": ["levy"],
            "interlachen": ["putnam"],
            "inverness": ["citrus"],
            "islamorada": ["monroe"],
            "jacksonville": ["duval"],
            "jacksonville beach": ["duval"],
            "jacob city": ["jackson"],
            "jasper": ["hamilton"],
            "jax": ["jacksonville", "duval"],
            "jay": ["santa rosa"],
            "jennings": ["hamilton"],
            "juno beach": ["palm beach"],
            "jupiter": ["palm beach"],
            "jupiter inlet colony": ["palm beach"],
            "jupiter island": ["martin"],
            "kenneth city": ["pinellas"],
            "key biscayne": ["miami-dade"],
            "key colony beach": ["monroe"],
            "key west": ["monroe"],
            "keystone heights": ["bradford", "clay"],
            "kissimmee": ["osceola"],
            "labelle": ["hendry"],
            "lacrosse": ["alachua"],
            "lady lake": ["lake"],
            "lake alfred": ["polk"],
            "lake buena vista": ["orange"],
            "lake butler": ["union"],
            "lake city": ["columbia"],
            "lake clarke shores": ["palm beach"],
            "lake hamilton": ["polk"],
            "lake helen": ["volusia"],
            "lake mary": ["seminole"],
            "lake park": ["palm beach"],
            "lake placid": ["highlands"],
            "lake wales": ["polk"],
            "lake worth": ["palm beach"],
            "lakeland": ["polk"],
            "lantana": ["palm beach"],
            "largo": ["pinellas"],
            "lauderdale lakes": ["broward"],
            "lauderdale-by-the-sea": ["broward"],
            "lauderhill": ["broward"],
            "laurel hill": ["okaloosa"],
            "lawtey nour town": ["bradford"],
            "layton": ["monroe"],
            "lazy lake": ["broward"],
            "lee": ["madison"],
            "leesburg": ["lake"],
            "lighthouse point": ["broward"],
            "live oak": ["suwannee"],
            "longboat key": ["manatee", "sarasota"],
            "longwood": ["seminole"],
            "loxahatchee groves": ["palm beach"],
            "lynn haven": ["bay"],
            "macclenny": ["baker"],
            "madeira beach": ["pinellas"],
            "madison": ["madison"],
            "maitland": ["orange"],
            "malabar": ["brevard"],
            "malone": ["jackson"],
            "manalapan": ["palm beach"],
            "mangonia park": ["palm beach"],
            "marathon": ["monroe"],
            "marco island": ["collier"],
            "margate": ["broward"],
            "marianna": ["jackson"],
            "marineland": ["flagler", "st. johns"],
            "mary esther": ["okaloosa"],
            "mascotte": ["lake"],
            "mayo": ["lafayette"],
            "mcintosh": ["marion"],
            "medley": ["miami-dade"],
            "melbourne": ["brevard"],
            "melbourne beach": ["brevard"],
            "melbourne village": ["brevard"],
            "mexico beach": ["bay"],
            "miami": ["miami-dade"],
            "miami beach": ["miami-dade"],
            "miami gardens": ["miami-dade"],
            "miami lakes": ["miami-dade"],
            "miami shores": ["miami-dade"],
            "miami springs": ["miami-dade"],
            "micanopy": ["alachua"],
            "midway": ["gadsden"],
            "milton": ["santa rosa"],
            "minneola": ["lake"],
            "miramar": ["broward"],
            "monticello": ["jefferson"],
            "montverde": ["lake"],
            "moore haven": ["glades"],
            "mount dora": ["lake"],
            "mulberry": ["polk"],
            "naples": ["collier"],
            "neptune beach": ["duval"],
            "new port richey": ["pasco"],
            "new smyrna beach": ["volusia"],
            "newberry": ["alachua"],
            "niceville": ["okaloosa"],
            "noma": ["holmes"],
            "north bay village": ["miami-dade"],
            "north lauderdale": ["broward"],
            "north miami": ["miami-dade"],
            "north miami beach": ["miami-dade"],
            "north palm beach": ["palm beach"],
            "north port": ["sarasota"],
            "north redington beach": ["pinellas"],
            "oak hill": ["volusia"],
            "oakland": ["orange"],
            "oakland park": ["broward"],
            "ocala": ["marion"],
            "ocean breeze": ["martin"],
            "ocean ridge": ["palm beach"],
            "ocoee": ["orange"],
            "okeechobee": ["okeechobee"],
            "oldsmar": ["pinellas"],
            "opa-locka": ["miami-dade"],
            "orange city": ["volusia"],
            "orange park": ["clay"],
            "orchid": ["indian river"],
            "orlando": ["orange"],
            "ormond beach": ["volusia"],
            "otter creek": ["levy"],
            "oviedo": ["seminole"],
            "pahokee": ["palm beach"],
            "palatka": ["putnam"],
            "palm bay": ["brevard"],
            "palm beach": ["palm beach"],
            "palm beach gardens": ["palm beach"],
            "palm beach shores": ["palm beach"],
            "palm coast": ["flagler"],
            "palm shores": ["brevard"],
            "palm springs": ["palm beach"],
            "palmetto": ["manatee"],
            "palmetto bay": ["miami-dade"],
            "panama city": ["bay"],
            "panama city beach": ["bay"],
            "parker": ["bay"],
            "parkland": ["broward"],
            "paxton": ["walton"],
            "pembroke park": ["broward"],
            "pembroke pines": ["broward"],
            "penney farms": ["clay"],
            "pensacola": ["escambia"],
            "perry": ["taylor"],
            "pierson": ["volusia"],
            "pinecrest": ["miami-dade"],
            "pinellas park": ["pinellas"],
            "plant city": ["hillsborough"],
            "plantation": ["broward"],
            "polk city": ["polk"],
            "pomona park": ["putnam"],
            "pompano beach": ["broward"],
            "ponce de leon": ["holmes"],
            "ponce inlet": ["volusia"],
            "port orange": ["volusia"],
            "port richey": ["pasco"],
            "port st. joe": ["gulf"],
            "port st. lucie": ["st. lucie"],
            "punta gorda": ["charlotte"],
            "quincy": ["gadsden"],
            "raiford": ["union"],
            "reddick": ["marion"],
            "redington beach": ["pinellas"],
            "redington shores": ["pinellas"],
            "riviera beach": ["palm beach"],
            "rockledge": ["brevard"],
            "royal palm beach": ["palm beach"],
            "safety harbor": ["pinellas"],
            "san antonio": ["pasco"],
            "sanford": ["seminole"],
            "sanibel": ["lee"],
            "sarasota": ["sarasota"],
            "satellite beach": ["brevard"],
            "sea ranch lakes": ["broward"],
            "sebastian": ["indian river"],
            "sebring": ["highlands"],
            "seminole": ["pinellas"],
            "sewall's point": ["martin"],
            "shalimar": ["okaloosa"],
            "sneads": ["jackson"],
            "sopchoppy": ["wakulla"],
            "south bay": ["palm beach"],
            "south daytona": ["volusia"],
            "south miami": ["miami-dade"],
            "south palm beach": ["palm beach"],
            "south pasadena": ["pinellas"],
            "southwest ranches": ["broward"],
            "springfield": ["bay"],
            "st augustine": ["st. johns"],
            "st augustine beach": ["st. johns"],
            "st cloud": ["osceola"],
            "st leo": ["pasco"],
            "st lucie village": ["st. lucie"],
            "st marks": ["wakulla"],
            "st pete beach": ["pinellas"],
            "st petersburg": ["pinellas"],
            "starke": ["bradford"],
            "stuart": ["martin"],
            "sunny isles beach": ["miami-dade"],
            "sunrise": ["broward"],
            "surfside": ["miami-dade"],
            "sweetwater": ["miami-dade"],
            "tallahassee": ["leon"],
            "tamarac": ["broward"],
            "tampa": ["hillsborough"],
            "tarpon springs": ["pinellas"],
            "tavares": ["lake"],
            "temple terrace": ["hillsborough"],
            "tequesta": ["palm beach"],
            "titusville": ["brevard"],
            "treasure island": ["pinellas"],
            "trenton": ["gilchrist"],
            "umatilla": ["lake"],
            "valparaiso": ["okaloosa"],
            "venice": ["sarasota"],
            "vernon": ["washington"],
            "vero beach": ["indian river"],
            "virginia gardens": ["miami-dade"],
            "waldo": ["alachua"],
            "wauchula": ["hardee"],
            "wausau": ["washington"],
            "webster": ["sumter"],
            "weeki wachee": ["hernando"],
            "welaka": ["putnam"],
            "wellington": ["palm beach"],
            "west melbourne": ["brevard"],
            "west miami": ["miami-dade"],
            "west palm beach": ["palm beach"],
            "west park": ["broward"],
            "weston": ["broward"],
            "westville": ["holmes"],
            "wewahitchka": ["gulf"],
            "white springs": ["hamilton"],
            "wildwood": ["sumter"],
            "williston": ["levy"],
            "wilton manors": ["broward"],
            "windermere": ["orange"],
            "winter garden": ["orange"],
            "winter haven": ["polk"],
            "winter park": ["orange"],
            "winter springs": ["seminole"],
            "worthington springs": ["union"],
            "yankeetown": ["levy"],
            "zephyrhills": ["pasco"],
            "zolfo springs": ["hardee"],
        };

        this.c0KeywordExpansions = {
            bals: ["Bay Area Legal Services"],
            bcla: ["Brevard County Legal Aid"],
            clp: ["Community Law Program"],
            clsmf: ["Community Legal Services Mid-Florida"],
            fiac: ["Americans for Immigrant Justice"],
            fji: ["Florida Justice Institute"],
            frls: ["Florida Rural Legal Services"],
            gls: ["Gulfcoast Legal Services"],
            jala: ["Jacksonville Area Legal Aid"],
            lam: ["Legal Aid of Manasota"],
            lasbc: ["Legal Aid Service of Broward County"],
            laspbc: ["Legal Aid Society of Palm Beach County"],
            lsgmi: ["Legal Services of Greater Miami"],
            scla: ["Seminole County Bar Association Legal Aid Society"],
            trls: ["Three Rivers Legal Services"],
        };

        // Expansions specifically for Chort 1
        // tslint:disable:object-literal-sort-keys
        this.c1CityCountyMappings = {
            "alameda": ["alameda"],
            "albany": ["alameda"],
            "amador city": ["alameda"],
            "berkeley": ["alameda"],
            "dublin": ["alameda"],
            "emeryville": ["alameda"],
            "fremont": ["alameda"],
            "hayward": ["alameda"],
            "livermore": ["alameda"],
            "newark": ["alameda"],
            "oakland": ["alameda"],
            "piedmont": ["alameda"],
            "pleasanton": ["alameda"],
            "san leandro": ["alameda"],
            "union city": ["alameda"],
            "ione": ["alameda"],
            "jackson": ["alameda"],
            "plymouth": ["alameda"],
            "sutter creek": ["alameda"],
            "biggs": ["butte"],
            "chico": ["butte"],
            "gridley": ["butte"],
            "oroville": ["butte"],
            "paradise": ["butte"],
            "angels camp": ["calaveras"],
            "colusa": ["colusa"],
            "williams": ["colusa"],
            "antioch": ["contra costa"],
            "brentwood": ["contra costa"],
            "clayton": ["contra costa"],
            "concord": ["contra costa"],
            "danville": ["contra costa"],
            "el cerrito": ["contra costa"],
            "hercules": ["contra costa"],
            "lafayette": ["contra costa"],
            "martinez": ["contra costa"],
            "moraga": ["contra costa"],
            "oakley": ["contra costa"],
            "orinda": ["contra costa"],
            "pinole": ["contra costa"],
            "pittsburg": ["contra costa"],
            "pleasant hill": ["contra costa"],
            "richmond": ["contra costa"],
            "san pablo": ["contra costa"],
            "san ramon": ["contra costa"],
            "walnut creek": ["contra costa"],
            "crescent city": ["del norte"],
            "placerville": ["el dorado"],
            "south lake tahoe": ["el dorado"],
            "clovis": ["fresno"],
            "coalinga": ["fresno"],
            "firebaugh": ["fresno"],
            "fowler": ["fresno"],
            "fresno": ["fresno"],
            "huron": ["fresno"],
            "kerman": ["fresno"],
            "kingsburg": ["fresno"],
            "mendota": ["fresno"],
            "orange cove": ["fresno"],
            "parlier": ["fresno"],
            "reedley": ["fresno"],
            "san joaquin": ["fresno"],
            "sanger": ["fresno"],
            "selma": ["fresno"],
            "orland": ["glenn"],
            "willows": ["glenn"],
            "arcata": ["humboldt"],
            "blue lake": ["humboldt"],
            "eureka": ["humboldt"],
            "ferndale": ["humboldt"],
            "fortuna": ["humboldt"],
            "rio dell": ["humboldt"],
            "trinidad": ["humboldt"],
            "brawley": ["imperial"],
            "calexico": ["imperial"],
            "calipatria": ["imperial"],
            "el centro": ["imperial"],
            "holtville": ["imperial"],
            "imperial": ["imperial"],
            "westmorland": ["imperial"],
            "bishop": ["inyo"],
            "arvin": ["kern"],
            "bakersfield": ["kern"],
            "california city": ["kern"],
            "delano": ["kern"],
            "maricopa": ["kern"],
            "mcfarland": ["kern"],
            "ridgecrest": ["kern"],
            "shafter": ["kern"],
            "taft": ["kern"],
            "tehachapi": ["kern"],
            "wasco": ["kern"],
            "avenal": ["kings"],
            "corcoran": ["kings"],
            "hanford": ["kings"],
            "lemoore": ["kings"],
            "clearlake": ["lake"],
            "lakeport": ["lake"],
            "susanville": ["lassen"],
            "agoura hills": ["los angeles"],
            "alhambra": ["los angeles"],
            "arcadia": ["los angeles"],
            "artesia": ["los angeles"],
            "avalon": ["los angeles"],
            "azusa": ["los angeles"],
            "baldwin park": ["los angeles"],
            "bell": ["los angeles"],
            "bell gardens": ["los angeles"],
            "bellflower": ["los angeles"],
            "beverly hills": ["los angeles"],
            "bradbury": ["los angeles"],
            "burbank": ["los angeles"],
            "calabasas": ["los angeles"],
            "carson": ["los angeles"],
            "cerritos": ["los angeles"],
            "claremont": ["los angeles"],
            "commerce": ["los angeles"],
            "compton": ["los angeles"],
            "covina": ["los angeles"],
            "cudahy": ["los angeles"],
            "culver city": ["los angeles"],
            "diamond bar": ["los angeles"],
            "downey": ["los angeles"],
            "duarte": ["los angeles"],
            "el monte": ["los angeles"],
            "el segundo": ["los angeles"],
            "gardena": ["los angeles"],
            "glendale": ["los angeles"],
            "glendora": ["los angeles"],
            "hawaiian gardens": ["los angeles"],
            "hawthorne": ["los angeles"],
            "hermosa beach": ["los angeles"],
            "hidden hills": ["los angeles"],
            "huntington park": ["los angeles"],
            "industry": ["los angeles"],
            "inglewood": ["los angeles"],
            "irwindale": ["los angeles"],
            "la cañada flintridge": ["los angeles"],
            "la habra heights": ["los angeles"],
            "la mirada": ["los angeles"],
            "la puente": ["los angeles"],
            "la verne": ["los angeles"],
            "lakewood": ["los angeles"],
            "lancaster": ["los angeles"],
            "lawndale": ["los angeles"],
            "lomita": ["los angeles"],
            "long beach": ["los angeles"],
            "los angeles": ["los angeles"],
            "lynwood": ["los angeles"],
            "malibu": ["los angeles"],
            "manhattan beach": ["los angeles"],
            "maywood": ["los angeles"],
            "monrovia": ["los angeles"],
            "montebello": ["los angeles"],
            "monterey park": ["los angeles"],
            "norwalk": ["los angeles"],
            "palmdale": ["los angeles"],
            "palos verdes estates": ["los angeles"],
            "paramount": ["los angeles"],
            "pasadena": ["los angeles"],
            "pico rivera": ["los angeles"],
            "pomona": ["los angeles"],
            "rancho palos verdes": ["los angeles"],
            "redondo beach": ["los angeles"],
            "rolling hills": ["los angeles"],
            "rolling hills estates": ["los angeles"],
            "rosemead": ["los angeles"],
            "san dimas": ["los angeles"],
            "san fernando": ["los angeles"],
            "san gabriel": ["los angeles"],
            "san marino": ["los angeles"],
            "santa clarita": ["los angeles"],
            "santa fe springs": ["los angeles"],
            "santa monica": ["los angeles"],
            "sierra madre": ["los angeles"],
            "signal hill": ["los angeles"],
            "south el monte": ["los angeles"],
            "south gate": ["los angeles"],
            "south pasadena": ["los angeles"],
            "temple city": ["los angeles"],
            "torrance": ["los angeles"],
            "vernon": ["los angeles"],
            "walnut": ["los angeles"],
            "west covina": ["los angeles"],
            "west hollywood": ["los angeles"],
            "westlake village": ["los angeles"],
            "whittier": ["los angeles"],
            "chowchilla": ["madera"],
            "madera": ["madera"],
            "belvedere": ["marin"],
            "corte madera": ["marin"],
            "fairfax": ["marin"],
            "larkspur": ["marin"],
            "mill valley": ["marin"],
            "novato": ["marin"],
            "ross": ["marin"],
            "san anselmo": ["marin"],
            "san rafael": ["marin"],
            "sausalito": ["marin"],
            "tiburon": ["marin"],
            "fort bragg": ["mendocino"],
            "point arena": ["mendocino"],
            "ukiah": ["mendocino"],
            "willits": ["mendocino"],
            "atwater": ["merced"],
            "dos palos": ["merced"],
            "gustine": ["merced"],
            "livingston": ["merced"],
            "los banos": ["merced"],
            "merced": ["merced"],
            "alturas": ["modoc"],
            "mammoth lakes": ["mono"],
            "carmel-by-the-sea": ["monterey"],
            "del rey oaks": ["monterey"],
            "gonzales": ["monterey"],
            "greenfield": ["monterey"],
            "king city": ["monterey"],
            "marina": ["monterey"],
            "monterey": ["monterey"],
            "pacific grove": ["monterey"],
            "salinas": ["monterey"],
            "sand city": ["monterey"],
            "seaside": ["monterey"],
            "soledad": ["monterey"],
            "american canyon": ["napa"],
            "calistoga": ["napa"],
            "napa": ["napa"],
            "st. helena": ["napa"],
            "yountville": ["napa"],
            "grass valley": ["nevada"],
            "nevada city": ["nevada"],
            "truckee": ["nevada"],
            "aliso viejo": ["orange"],
            "anaheim": ["orange"],
            "brea": ["orange"],
            "buena park": ["orange"],
            "costa mesa": ["orange"],
            "cypress": ["orange"],
            "dana point": ["orange"],
            "fountain valley": ["orange"],
            "fullerton": ["orange"],
            "garden grove": ["orange"],
            "huntington beach": ["orange"],
            "irvine": ["orange"],
            "la habra": ["orange"],
            "la palma": ["orange"],
            "laguna beach": ["orange"],
            "laguna hills": ["orange"],
            "laguna niguel": ["orange"],
            "laguna woods": ["orange"],
            "lake forest": ["orange"],
            "los alamitos": ["orange"],
            "mission viejo": ["orange"],
            "newport beach": ["orange"],
            "orange": ["orange"],
            "placentia": ["orange"],
            "rancho santa margarita": ["_orange_"],
            "san clemente": ["orange"],
            "san juan capistrano": ["orange"],
            "santa ana": ["orange"],
            "seal beach": ["orange"],
            "stanton": ["orange"],
            "tustin": ["orange"],
            "villa park": ["orange"],
            "westminster": ["orange"],
            "yorba linda": ["orange"],
            "auburn": ["placer"],
            "colfax": ["placer"],
            "lincoln": ["placer"],
            "loomis": ["placer"],
            "rocklin": ["placer"],
            "roseville": ["placer"],
            "portola": ["plumas"],
            "banning": ["riverside"],
            "beaumont": ["riverside"],
            "blythe": ["riverside"],
            "calimesa": ["riverside"],
            "canyon lake": ["riverside"],
            "cathedral city": ["riverside"],
            "coachella": ["riverside"],
            "corona": ["riverside"],
            "desert hot springs": ["riverside"],
            "eastvale": ["riverside"],
            "hemet": ["riverside"],
            "indian wells": ["riverside"],
            "indio": ["riverside"],
            "jurupa valley": ["riverside"],
            "la quinta": ["riverside"],
            "lake elsinore": ["riverside"],
            "menifee": ["riverside"],
            "moreno valley": ["riverside"],
            "murrieta": ["riverside"],
            "norco": ["riverside"],
            "palm desert": ["riverside"],
            "palm springs": ["riverside"],
            "perris": ["riverside"],
            "rancho mirage": ["riverside"],
            "riverside": ["riverside"],
            "san jacinto": ["riverside"],
            "temecula": ["riverside"],
            "wildomar": ["riverside"],
            "citrus heights": ["sacramento"],
            "elk grove": ["sacramento"],
            "folsom": ["sacramento"],
            "galt": ["sacramento"],
            "isleton": ["sacramento"],
            "rancho cordova": ["sacramento"],
            "sacramento": ["sacramento"],
            "hollister": ["san benito"],
            "san juan bautista": ["san benito"],
            "adelanto": ["san bernardino"],
            "apple valley": ["san bernardino"],
            "barstow": ["san bernardino"],
            "big bear lake": ["san bernardino"],
            "chino": ["san bernardino"],
            "chino hills": ["san bernardino"],
            "colton": ["san bernardino"],
            "fontana": ["san bernardino"],
            "grand terrace": ["san bernardino"],
            "hesperia": ["san bernardino"],
            "highland": ["san bernardino"],
            "loma linda": ["san bernardino"],
            "montclair": ["san bernardino"],
            "needles": ["san bernardino"],
            "ontario": ["san bernardino"],
            "rancho cucamonga": ["san bernardino"],
            "redlands": ["san bernardino"],
            "rialto": ["san bernardino"],
            "san bernardino": ["san bernardino"],
            "twentynine palms": ["san bernardino"],
            "upland": ["san bernardino"],
            "victorville": ["san bernardino"],
            "yucaipa": ["san bernardino"],
            "yucca valley": ["san bernardino"],
            "carlsbad": ["san diego"],
            "chula vista": ["san diego"],
            "coronado": ["san diego"],
            "del mar": ["san diego"],
            "el cajon": ["san diego"],
            "encinitas": ["san diego"],
            "escondido": ["san diego"],
            "imperial beach": ["san diego"],
            "la mesa": ["san diego"],
            "lemon grove": ["san diego"],
            "national city": ["san diego"],
            "oceanside": ["san diego"],
            "poway": ["san diego"],
            "san diego": ["san diego"],
            "san marcos": ["san diego"],
            "santee": ["san diego"],
            "solana beach": ["san diego"],
            "vista": ["san diego"],
            "san francisco": ["san francisco"],
            "escalon": ["san joaquin"],
            "lathrop": ["san joaquin"],
            "lodi": ["san joaquin"],
            "manteca": ["san joaquin"],
            "ripon": ["san joaquin"],
            "stockton": ["san joaquin"],
            "tracy": ["san joaquin"],
            "arroyo grande": ["san luis obispo"],
            "atascadero": ["san luis obispo"],
            "grover beach": ["san luis obispo"],
            "morro bay": ["san luis obispo"],
            "paso robles": ["san luis obispo"],
            "pismo beach": ["san luis obispo"],
            "san luis obispo": ["san luis obispo"],
            "atherton": ["san mateo"],
            "belmont": ["san mateo"],
            "brisbane": ["san mateo"],
            "burlingame": ["san mateo"],
            "colma": ["san mateo"],
            "daly city": ["san mateo"],
            "east palo alto": ["san mateo"],
            "foster city": ["san mateo"],
            "half moon bay": ["san mateo"],
            "hillsborough": ["san mateo"],
            "menlo park": ["san mateo"],
            "millbrae": ["san mateo"],
            "pacifica": ["san mateo"],
            "portola valley": ["san mateo"],
            "redwood city": ["san mateo"],
            "san bruno": ["san mateo"],
            "san carlos": ["san mateo"],
            "san mateo": ["san mateo"],
            "south san francisco": ["san mateo"],
            "woodside": ["san mateo"],
            "buellton": ["santa barbara"],
            "carpinteria": ["santa barbara"],
            "goleta": ["santa barbara"],
            "guadalupe": ["santa barbara"],
            "lompoc": ["santa barbara"],
            "santa barbara": ["santa barbara"],
            "santa maria": ["santa barbara"],
            "solvang": ["santa barbara"],
            "campbell": ["santa clara"],
            "cupertino": ["santa clara"],
            "gilroy": ["santa clara"],
            "los altos": ["santa clara"],
            "los altos hills": ["santa clara"],
            "los gatos": ["santa clara"],
            "milpitas": ["santa clara"],
            "monte sereno": ["santa clara"],
            "morgan hill": ["santa clara"],
            "mountain view": ["santa clara"],
            "palo alto": ["santa clara"],
            "san jose": ["santa clara"],
            "santa clara": ["santa clara"],
            "saratoga": ["santa clara"],
            "sunnyvale": ["santa clara"],
            "capitola": ["santa cruz"],
            "santa cruz": ["santa cruz"],
            "scotts valley": ["santa cruz"],
            "watsonville": ["santa cruz"],
            "anderson": ["shasta"],
            "redding": ["shasta"],
            "shasta lake": ["shasta"],
            "loyalton": ["siskiyou"],
            "dorris": ["siskiyou"],
            "dunsmuir": ["siskiyou"],
            "etna": ["siskiyou"],
            "fort jones": ["siskiyou"],
            "montague": ["siskiyou"],
            "mount shasta": ["siskiyou"],
            "tulelake": ["siskiyou"],
            "weed": ["siskiyou"],
            "yreka": ["siskiyou"],
            "benicia": ["solano"],
            "dixon": ["solano"],
            "fairfield": ["solano"],
            "rio vista": ["solano"],
            "suisun city": ["solano"],
            "vacaville": ["solano"],
            "vallejo": ["solano"],
            "cloverdale": ["sonoma"],
            "cotati": ["sonoma"],
            "healdsburg": ["sonoma"],
            "petaluma": ["sonoma"],
            "rohnert park": ["sonoma"],
            "santa rosa": ["sonoma"],
            "sebastopol": ["sonoma"],
            "sonoma": ["sonoma"],
            "windsor": ["sonoma"],
            "ceres": ["stanislaus"],
            "hughson": ["stanislaus"],
            "modesto": ["stanislaus"],
            "newman": ["stanislaus"],
            "oakdale": ["stanislaus"],
            "patterson": ["stanislaus"],
            "riverbank": ["stanislaus"],
            "turlock": ["stanislaus"],
            "waterford": ["stanislaus"],
            "live oak": ["sutter"],
            "yuba city": ["sutter"],
            "corning": ["tehama"],
            "red bluff": ["tehama"],
            "tehama": ["tehama"],
            "dinuba": ["tulare"],
            "exeter": ["tulare"],
            "farmersville": ["tulare"],
            "lindsay": ["tulare"],
            "porterville": ["tulare"],
            "tulare": ["tulare"],
            "visalia": ["tulare"],
            "woodlake": ["tulare"],
            "sonora": ["tuolumne"],
            "camarillo": ["ventura"],
            "fillmore": ["ventura"],
            "moorpark": ["ventura"],
            "ojai": ["ventura"],
            "oxnard": ["ventura"],
            "port hueneme": ["ventura"],
            "santa paula": ["ventura"],
            "simi valley": ["ventura"],
            "thousand oaks": ["ventura"],
            "ventura": ["ventura"],
            "davis": ["yolo"],
            "west sacramento": ["yolo"],
            "winters": ["yolo"],
            "woodland": ["yolo"],
            "marysville": ["yolo"],
            "wheatland": ["yuba"],
        };

        this.c1KeywordExpansions = {};

        // Expansions specifically for Cohort 2
        // tslint:disable:object-literal-sort-keys
        this.c2CityCountyMappings = {
            "portland": ["multnomah", "washington", "clackamas"],
            "salem": ["marion", "polk"],
            "eugene": ["lane"],
            "gresham": ["multnomah"],
            "hillsboro": ["washington"],
            "beaverton": ["washington"],
            "bend": ["deschutes"],
            "medford": ["jackson"],
            "springfield": ["lane"],
            "corvallis": ["benton"],
            "albany": ["linn, benton"],
            "tigard": ["washington"],
            "lake oswego": ["clackamas", "multnomah", "washington"],
            "keizer": ["marion"],
            "grants pass": ["josephine"],
            "oregon city": ["clackamas"],
            "mcminnville": ["yamhill"],
            "redmond": ["deschutes"],
            "tualatin": ["washington", "clackamas"],
            "west linn": ["clackamas"],
            "woodburn": ["marion"],
            "forest grove": ["washington"],
            "newberg": ["yamhill"],
            "wilsonville": ["clackamas", "washington"],
            "roseburg": ["douglas"],
            "klamath falls": ["klamath"],
            "ashland": ["jackson"],
            "milwaukie": ["clackamas", "multnomah"],
            "sherwood": ["washington"],
            "happy valley": ["clackamas"],
            "central point": ["jackson"],
            "canby": ["clackamas"],
            "hermiston": ["umatilla"],
            "pendleton": ["umatilla"],
            "troutdale": ["multnomah"],
            "lebanon": ["linn"],
            "coos bay": ["coos"],
            "the dalles": ["wasco"],
            "dallas": ["polk"],
            "st. helens": ["columbia"],
            "la grande": ["union"],
            "cornelius": ["washington"],
            "gladstone": ["clackamas"],
            "ontario": ["malheur"],
            "sandy": ["clackamas"],
            "newport": ["lincoln"],
            "monmouth": ["polk"],
            "cottage grove": ["lane"],
            "silverton": ["marion"],
            "baker city": ["baker"],
            "north bend": ["coos"],
            "astoria": ["clatsop"],
            "prineville": ["crook"],
            "fairview": ["multnomah"],
            "sweet home": ["linn"],
            "independence": ["polk"],
            "molalla": ["clackamas"],
            "eagle point": ["jackson"],
            "florence": ["lane"],
            "lincoln city": ["lincoln"],
            "stayton": ["marion"],
            "sutherlin": ["douglas"],
            "hood river": ["hood river"],
            "milton-freewater": ["umatilla"],
            "umatilla": ["umatilla"],
            "scappoose": ["columbia"],
            "madras": ["jefferson"],
            "seaside": ["clatsop"],
            "brookings": ["curry"],
            "talent": ["jackson"],
            "sheridan": ["yamhill"],
            "junction city": ["lane"],
            "winston": ["douglas"],
            "warrenton": ["clatsop"],
            "creswell": ["lane"],
            "tillamook": ["tillamook"],
            "veneta": ["lane"],
            "philomath": ["benton"],
            "phoenix": ["jackson"],
            "reedsport": ["douglas"],
            "wood village": ["multnomah"],
            "aumsville": ["marion"],
            "lafayette": ["yamhill"],
            "coquille": ["coos"],
            "harrisburg": ["linn"],
            "king city": ["washington"],
            "toledo": ["lincoln"],
            "myrtle creek": ["douglas"],
            "mt. angel": ["marion"],
            "boardman": ["morrow"],
            "hubbard": ["marion"],
            "jefferson": ["marion"],
            "oakridge": ["lane"],
            "dundee": ["yamhill"],
            "nyssa": ["malheur"],
            "estacada": ["clackamas"],
            "bandon": ["coos"],
            "shady cove": ["jackson"],
            "jacksonville": ["jackson"],
            "sublimity": ["marion"],
            "burns": ["harney"],
            "gervais": ["marion"],
            "dayton": ["yamhill"],
            "myrtle point": ["coos"],
            "sisters": ["deschutes"],
            "lakeview": ["lake"],
            "gold beach": ["curry"],
            "rogue river": ["jackson"],
            "vernonia": ["columbia"],
            "waldport": ["lincoln"],
            "union": ["union"],
            "willamina": ["yamhill", "polk"],
            "north plains": ["washington"],
            "stanfield": ["umatilla"],
            "carlton": ["yamhill"],
            "turner": ["marion"],
            "columbia city": ["columbia"],
            "durham": ["washington"],
            "banks": ["washington"],
            "cave junction": ["josephine"],
            "rainier": ["columbia"],
            "canyonville": ["douglas"],
            "enterprise": ["wallowa"],
            "mill city": ["marion", "linn"],
            "vale †": ["malheur"],
            "irrigon": ["morrow"],
            "la pine": ["deschutes"],
            "clatskanie": ["columbia"],
            "brownsville": ["linn"],
            "lakeside": ["coos"],
            "elgin": ["union"],
            "cannon beach": ["clatsop"],
            "john day": ["grant"],
            "amity": ["yamhill"],
            "millersburg": ["linn"],
            "hines": ["harney"],
            "gearhart": ["clatsop"],
            "pilot rock": ["umatilla"],
            "culver": ["jefferson"],
            "depoe bay": ["lincoln"],
            "rockaway beach": ["tillamook"],
            "dunes city": ["lane"],
            "bay city": ["tillamook"],
            "heppner": ["morrow"],
            "gold hill": ["jackson"],
            "siletz": ["lincoln"],
            "tangent": ["linn"],
            "riddle": ["douglas"],
            "lyons": ["linn"],
            "cascade locks": ["hood river"],
            "drain": ["douglas"],
            "port orford": ["curry"],
            "athena": ["umatilla"],
            "yamhill": ["yamhill"],
            "lowell": ["lane"],
            "joseph": ["wallowa"],
            "coburg": ["lane"],
            "yoncalla": ["douglas"],
            "island city": ["union"],
            "donald": ["marion"],
            "aurora": ["marion"],
            "falls city": ["polk"],
            "halsey": ["linn"],
            "oakland": ["douglas"],
            "scio": ["linn"],
            "glendale": ["douglas"],
            "prairie city": ["grant"],
            "merrill": ["klamath"],
            "adair village": ["benton"],
            "malin": ["klamath"],
            "wallowa": ["wallowa"],
            "garibaldi": ["tillamook"],
            "maywood park": ["multnomah"],
            "metolius": ["jefferson"],
            "chiloquin": ["klamath"],
            "yachats": ["lincoln"],
            "echo": ["umatilla"],
            "gaston": ["washington"],
            "condon": ["gilliam"],
            "canyon city": ["grant"],
            "powers": ["coos"],
            "weston": ["umatilla"],
            "dufur": ["wasco"],
            "manzanita": ["tillamook"],
            "monroe": ["benton"],
            "arlington": ["gilliam"],
            "johnson city": ["clackamas"],
            "cove": ["union"],
            "mount vernon": ["grant"],
            "gates": ["marion", "linn"],
            "fossil": ["wheeler"],
            "mosier": ["wasco"],
            "st. paul": ["marion"],
            "huntington": ["baker"],
            "north powder": ["union"],
            "butte falls": ["jackson"],
            "maupin": ["wasco"],
            "wheeler": ["tillamook"],
            "haines": ["baker"],
            "bonanza": ["klamath"],
            "wasco": ["sherman"],
            "rivergrove": ["clackamas", "washington"],
            "scotts mills": ["marion"],
            "adams": ["umatilla"],
            "ione": ["morrow"],
            "sodaville": ["linn"],
            "moro": ["sherman"],
            "imbler": ["union"],
            "halfway": ["baker"],
            "nehalem": ["tillamook"],
            "westfir": ["lane"],
            "paisley": ["lake"],
            "rufus": ["sherman"],
            "lexington": ["morrow"],
            "waterloo": ["linn"],
            "detroit": ["marion"],
            "lostine": ["wallowa"],
            "sumpter": ["baker"],
            "elkton": ["douglas"],
            "seneca": ["grant"],
            "long creek": ["grant"],
            "ukiah": ["umatilla"],
            "helix": ["umatilla"],
            "richland": ["baker"],
            "jordan valley": ["malheur"],
            "adrian": ["malheur"],
            "grass valley": ["sherman"],
            "spray": ["wheeler"],
            "dayville": ["grant"],
            "barlow": ["clackamas"],
            "idanha": ["marion", "linn"],
            "summerville": ["union"],
            "monument": ["grant"],
            "mitchell": ["wheeler"],
            "unity": ["baker"],
            "prescott": ["columbia"],
            "antelope": ["wasco"],
            "granite": ["grant"],
            "shaniko": ["wasco"],
            "lonerock": ["gilliam"],
            "greenhorn": ["baker", "grant"],
        };

        this.c2KeywordExpansions = {};

        // Expansions specifically for Cohort 5
        // tslint:disable:object-literal-sort-keys
        this.c4CityCountyMappings = {
            "Alexandria": ["Alexandria"],
            "Bristol": ["Washington"],
            "Buena Vista": ["Rockbridge"],
            "Charlottesville": ["Albemarle"],
            "Colonial Heights": ["Chesterfield"],
            "Covington": ["Alleghany"],
            "Danville": ["Pittsylvania"],
            "Emporia": ["Greensville"],
            "Fairfax": ["Fairfax"],
            "Falls Church": ["Fairfax"],
            "Franklin": ["Southampton"],
            "Fredericksburg": ["Spotsylvania"],
            "Galax": ["Grayson", "Carroll"],
            "Harrisonburg": ["Rockingham"],
            "Hopewell": ["Prince George"],
            "Lexington": ["Rockbridge"],
            "Lynchburg": ["Campbell"],
            "Manassas": ["Prince William"],
            "Manassas Park": ["Prince William"],
            "Martinsville": ["Henry"],
            "Newport News": ["Warwick"],
            "Norfolk": [],
            "Norton": ["Wise"],
            "Petersburg": ["Prince George"],
            "Poquoson": ["York"],
            "Radford": ["Montgomery"],
            "Richmond": ["Henrico"],
            "Roanoke": ["Roanoke"],
            "Salem": ["Roanoke"],
            "Staunton": ["Augusta"],
            "Suffolk": [],
            "Virginia Beach": [""],
            "Waynesboro": ["Augusta"],
            "Williamsburg": ["James City"],
            "Winchester": ["Frederick"],
        };

        this.c4KeywordExpansions = {};

        // Expansions specifically for Cohort 5
        // tslint:disable:object-literal-sort-keys
        this.c5CityCountyMappings = {
            "adams": ["robertson"],
            "adamsville": ["mcnairy", "hardin"],
            "alamo": ["crockett"],
            "alcoa": ["blount"],
            "alexandria": ["dekalb"],
            "algood": ["putnam"],
            "allardt": ["fentress"],
            "altamont": ["grundy"],
            "ardmore": ["giles"],
            "arlington": ["shelby"],
            "ashland city": ["cheatham"],
            "athens": ["mcminn"],
            "atoka": ["tipton"],
            "atwood": ["carroll"],
            "auburntown": ["cannon"],
            "baileyton": ["greene"],
            "baneberry": ["jefferson"],
            "bartlett": ["shelby"],
            "baxter": ["putnam"],
            "bean station": ["grainger"],
            "beersheba springs": ["grundy"],
            "bell buckle": ["bedford"],
            "belle meade": ["davidson"],
            "bells": ["crockett"],
            "benton": ["polk"],
            "berry hill": ["davidson"],
            "bethel springs": ["mcnairy"],
            "big sandy": ["benton"],
            "blaine": ["grainger"],
            "bluff city": ["sullivan"],
            "bolivar": ["hardeman"],
            "braden": ["fayette"],
            "bradford": ["gibson"],
            "brentwood": ["williamson"],
            "brighton": ["tipton"],
            "bristol": ["sullivan"],
            "brownsville": ["haywood"],
            "bruceton": ["carroll"],
            "bulls gap": ["hawkins"],
            "burlison": ["tipton"],
            "burns": ["dickson"],
            "byrdstown": ["pickett"],
            "calhoun": ["mcminn"],
            "camden": ["benton"],
            "carthage": ["smith"],
            "caryville": ["campbell"],
            "cedar hill": ["robertson"],
            "celina": ["clay"],
            "centertown": ["warren"],
            "centerville": ["hickman"],
            "chapel hill": ["marshall"],
            "charleston": ["bradley"],
            "charlotte": ["dickson"],
            "chattanooga": ["hamilton"],
            "church hill": ["hawkins"],
            "clarksburg": ["carroll"],
            "clarksville": ["montgomery"],
            "cleveland": ["bradley"],
            "clifton": ["wayne"],
            "clinton": ["anderson"],
            "coalmont": ["grundy"],
            "collegedale": ["hamilton"],
            "collierville": ["shelby"],
            "collinwood": ["wayne"],
            "columbia": ["maury"],
            "cookeville": ["putnam"],
            "coopertown": ["robertson"],
            "copperhill": ["polk"],
            "cornersville": ["marshall"],
            "cottage grove": ["henry"],
            "covington": ["tipton"],
            "cowan": ["franklin"],
            "crab orchard": ["cumberland"],
            "cross plains": ["robertson"],
            "crossville": ["cumberland"],
            "crump": ["hardin"],
            "cumberland city": ["stewart"],
            "cumberland gap": ["claiborne"],
            "dandridge": ["jefferson"],
            "dayton": ["rhea"],
            "decatur": ["meigs"],
            "decaturville": ["decatur"],
            "decherd": ["franklin"],
            "dickson": ["dickson"],
            "dover": ["stewart"],
            "dowelltown": ["dekalb"],
            "doyle": ["white"],
            "dresden": ["weakley"],
            "ducktown": ["polk"],
            "dunlap": ["sequatchie"],
            "dyer": ["gibson"],
            "dyersburg": ["dyer"],
            "eagleville": ["rutherford"],
            "east ridge": ["hamilton"],
            "eastview": ["mcnairy"],
            "elizabethton": ["carter"],
            "elkton": ["giles"],
            "englewood": ["mcminn"],
            "enville": ["chester", "mcnairy"],
            "e'rin": ["houston"],
            "erin": ["houston"],
            "erwin": ["unicoi"],
            "estill springs": ["franklin"],
            "ethridge": ["lawrence"],
            "etowah": ["mcminn"],
            "fairview": ["williamson"],
            "farragut": ["knox"],
            "fayetteville": ["lincoln"],
            "finger": ["mcnairy"],
            "forest hills": ["davidson"],
            "franklin": ["williamson"],
            "friendship": ["crockett"],
            "friendsville": ["blount"],
            "gadsden": ["crockett"],
            "gainesboro": ["jackson"],
            "gallatin": ["sumner"],
            "gallaway": ["fayette"],
            "garland": ["tipton"],
            "gates": ["lauderdale"],
            "gatlinburg": ["sevier"],
            "germantown": ["shelby"],
            "gibson": ["gibson"],
            "gilt edge": ["tipton"],
            "gleason": ["weakley"],
            "goodlettsville": ["davidson", "sumner"],
            "gordonsville": ["smith"],
            "grand junction": ["hardeman", "fayette"],
            "graysville": ["rhea"],
            "greenback": ["loudon"],
            "greenbrier": ["robertson"],
            "greeneville": ["greene"],
            "greenfield": ["weakley"],
            "gruetli-laager": ["grundy"],
            "guys": ["mcnairy"],
            "halls": ["lauderdale"],
            "harriman": ["roane"],
            "harrogate": ["claiborne"],
            "hartsville": ["trousdale"],
            "henderson": ["chester"],
            "hendersonville": ["sumner"],
            "henning": ["lauderdale"],
            "henry": ["henry"],
            "hickory valley": ["hardeman"],
            "hohenwald": ["lewis"],
            "hollow rock": ["carroll"],
            "hornbeak": ["obion"],
            "hornsby": ["hardeman"],
            "humboldt": ["gibson"],
            "huntingdon": ["carroll"],
            "huntland": ["franklin"],
            "huntsville": ["scott"],
            "iron city": ["lawrence", "wayne"],
            "jacksboro": ["campbell"],
            "jackson": ["madison"],
            "jamestown": ["fentress"],
            "jasper": ["marion"],
            "jefferson city": ["jefferson"],
            "jellico": ["campbell"],
            "johnson city": ["washington", "carter", "sullivan"],
            "jonesborough": ["washington"],
            "kenton": ["gibson", "obion"],
            "kimball": ["marion"],
            "kingsport": ["sullivan", "hawkins"],
            "kingston": ["roane"],
            "kingston springs": ["cheatham"],
            "knoxville": ["knox"],
            "lafayette": ["macon"],
            "la follette": ["campbell"],
            "lagrange": ["fayette"],
            "lakeland": ["shelby"],
            "lakesite": ["hamilton"],
            "la vergne": ["rutherford"],
            "lawrenceburg": ["lawrence"],
            "lebanon": ["wilson"],
            "lenoir city": ["loudon"],
            "lewisburg": ["marshall"],
            "lexington": ["henderson"],
            "liberty": ["dekalb"],
            "linden": ["perry"],
            "livingston": ["overton"],
            "lobelville": ["perry"],
            "lookout mountain": ["hamilton"],
            "loretto": ["lawrence"],
            "loudon": ["loudon"],
            "louisville": ["blount"],
            "luttrell": ["union"],
            "lynchburg": ["moore"],
            "lynnville": ["giles"],
            "madisonville": ["monroe"],
            "manchester": ["coffee"],
            "martin": ["weakley"],
            "maryville": ["blount"],
            "mason": ["tipton"],
            "maury city": ["crockett"],
            "maynardville": ["union"],
            "mcewen": ["humphreys"],
            "mckenzie": ["carroll", "henry", "weakley"],
            "mclemoresville": ["carroll"],
            "mcminnville": ["warren"],
            "medina": ["gibson"],
            "medon": ["madison"],
            "memphis": ["shelby"],
            "michie": ["mcnairy"],
            "middleton": ["hardeman"],
            "milan": ["gibson"],
            "milledgeville": ["mcnairy", "chester", "hardin"],
            "millersville": ["sumner", "robertson"],
            "millington": ["shelby"],
            "minor hill": ["giles"],
            "mitchellville": ["sumner"],
            "monteagle": ["grundy", "marion"],
            "monterey": ["putnam"],
            "morrison": ["warren"],
            "morristown": ["hamblen"],
            "moscow": ["fayette"],
            "mosheim": ["greene"],
            "mount carmel": ["hawkins"],
            "mount juliet": ["wilson"],
            "mount pleasant": ["maury"],
            "mountain city": ["johnson"],
            "munford": ["tipton"],
            "murfreesboro": ["rutherford"],
            "nashville": ["davidson"],
            "new hope": ["marion"],
            "new johnsonville": ["humphreys"],
            "new market": ["jefferson"],
            "new tazewell": ["claiborne"],
            "newbern": ["dyer"],
            "newport": ["cocke"],
            "niota": ["mcminn"],
            "nolensville": ["williamson"],
            "normandy": ["bedford"],
            "norris": ["anderson"],
            "oak hill": ["davidson"],
            "oak ridge": ["anderson", "roane"],
            "oakdale": ["morgan"],
            "oakland": ["fayette"],
            "obion": ["obion"],
            "oliver springs": ["anderson", "morgan", "roane"],
            "oneida": ["scott"],
            "orlinda": ["robertson"],
            "orme": ["marion"],
            "palmer": ["grundy"],
            "paris": ["henry"],
            "parker's crossroads": ["henderson"],
            "parkers crossroads": ["henderson"],
            "parrottsville": ["cocke"],
            "parsons": ["decatur"],
            "pegram": ["cheatham"],
            "petersburg": ["lincoln", "marshall"],
            "philadelphia": ["loudon"],
            "pigeon forge": ["sevier"],
            "pikeville": ["bledsoe"],
            "piperton": ["fayette"],
            "pittman center": ["sevier"],
            "plainview": ["union"],
            "pleasant hill": ["cumberland"],
            "pleasant view": ["cheatham"],
            "portland": ["sumner"],
            "powell's crossroads": ["marion"],
            "powells crossroads": ["marion"],
            "pulaski": ["giles"],
            "puryear": ["henry"],
            "ramer": ["mcnairy"],
            "red bank": ["hamilton"],
            "red boiling springs": ["macon"],
            "ridgely": ["lake"],
            "ridgeside": ["hamilton"],
            "ridgetop": ["robertson", "davidson"],
            "ripley": ["lauderdale"],
            "rives": ["obion"],
            "rockford": ["blount"],
            "rockwood": ["roane"],
            "rocky top": ["anderson", "campbell"],
            "rogersville": ["hawkins"],
            "rossville": ["fayette"],
            "rutherford": ["gibson"],
            "rutledge": ["grainger"],
            "st. joseph": ["lawrence"],
            "saltillo": ["hardin"],
            "samburg": ["obion"],
            "sardis": ["henderson"],
            "saulsbury": ["hardeman"],
            "savannah": ["hardin"],
            "scotts hill": ["henderson", "decatur"],
            "selmer": ["mcnairy"],
            "sevierville": ["sevier"],
            "sharon": ["weakley"],
            "shelbyville": ["bedford"],
            "signal mountain": ["hamilton"],
            "silerton": ["hardeman", "chester"],
            "slayden": ["dickson"],
            "smithville": ["dekalb"],
            "smyrna": ["rutherford"],
            "sneedville": ["hancock"],
            "soddy-daisy": ["hamilton"],
            "somerville": ["fayette"],
            "south carthage": ["smith"],
            "south fulton": ["obion"],
            "south pittsburg": ["marion"],
            "sparta": ["white"],
            "spencer": ["van buren"],
            "spring city": ["rhea"],
            "spring hill": ["maury", "williamson"],
            "springfield": ["robertson"],
            "stanton": ["haywood"],
            "stantonville": ["mcnairy"],
            "sunbright": ["morgan"],
            "surgoinsville": ["hawkins"],
            "sweetwater": ["monroe", "mcminn"],
            "tazewell": ["claiborne"],
            "tellico plains": ["monroe"],
            "tennessee ridge": ["houston", "stewart"],
            "thompson's station": ["williamson"],
            "thompsons station": ["williamson"],
            "three way": ["madison"],
            "tiptonville": ["lake"],
            "toone": ["hardeman"],
            "townsend": ["blount"],
            "tracy city": ["grundy"],
            "trenton": ["gibson"],
            "trezevant": ["carroll"],
            "trimble": ["dyer", "obion"],
            "troy": ["obion"],
            "tullahoma": ["coffee", "franklin"],
            "tusculum": ["greene"],
            "unicoi": ["unicoi"],
            "union city": ["obion"],
            "vanleer": ["dickson"],
            "viola": ["warren"],
            "vonore": ["monroe"],
            "walden": ["hamilton"],
            "wartburg": ["morgan"],
            "wartrace": ["bedford"],
            "watauga": ["carter"],
            "watertown": ["wilson"],
            "waverly": ["humphreys"],
            "waynesboro": ["wayne"],
            "westmoreland": ["sumner"],
            "white bluff": ["dickson"],
            "white house": ["sumner", "robertson"],
            "white pine": ["jefferson"],
            "whiteville": ["hardeman"],
            "whitwell": ["marion"],
            "williston": ["fayette"],
            "winchester": ["franklin"],
            "winfield": ["scott"],
            "woodbury": ["cannon"],
            "woodland mills": ["obion"],
            "yorkville": ["gibson count"],
        };

        this.c5KeywordExpansions = {};

        // Expansions specifically for Cohort 7
        // tslint:disable:object-literal-sort-keys
        this.c7CityCountyMappings = {};

        this.c7KeywordExpansions = {};

        // Expansions specifically for Cohort 8
        // tslint:disable:object-literal-sort-keys
        this.c8CityCountyMappings = {
            "aberdeen": ["moore county"],
            "ahoskie": ["hertford county"],
            "alamance": ["alamance county"],
            "albemarle": ["stanly county county"],
            "alliance": ["pamlico county county"],
            "andrews": ["cherokee county"],
            "angier": ["harnett county", "wake county"],
            "anson countyville": ["anson county"],
            "apex": ["wake county"],
            "arapahoe": ["pamlico county"],
            "archdale": ["randolph county", "guilford county"],
            "archer lodge": ["johnston county"],
            "asheboro": ["randolph county"],
            "asheville": ["buncombe county"],
            "askewville": ["bertie county"],
            "atkinson": ["pender county"],
            "atlantic beach": ["carteret county"],
            "aulander": ["bertie county"],
            "aurora": ["beaufort county"],
            "autryville": ["sampson county"],
            "ayden": ["pitt county"],
            "badin": ["stanly county"],
            "bailey": ["nash county"],
            "bakersville": ["mitchell county"],
            "bald head island": ["brunswick county"],
            "banner elk": ["avery county"],
            "bath": ["beaufort county"],
            "bayboro": ["pamlico county"],
            "bear grass": ["martin county"],
            "beaufort": ["carteret county"],
            "beech mountain": ["watauga county", "avery county"],
            "belhaven": ["beaufort county"],
            "belmont": ["gaston county"],
            "belville": ["brunswick county"],
            "belwood": ["cleveland county"],
            "benson": ["johnston county"],
            "bermuda run": ["davie county"],
            "bessemer city": ["gaston county"],
            "bethania": ["forsyth county"],
            "bethel": ["pitt county"],
            "beulaville": ["duplin county"],
            "biltmore forest": ["buncombe county"],
            "biscoe": ["montgomery county"],
            "black creek": ["wilson county"],
            "black mountain": ["buncombe county"],
            "bladenboro": ["bladen county"],
            "blowing rock": ["watauga county","caldwell county"],
            "boardman": ["columbus county"],
            "bogue": ["carteret county"],
            "boiling spring lakes": ["brunswick county"],
            "boiling springs": ["cleveland county"],
            "bolivia": ["brunswick county"],
            "bolton": ["columbus county"],
            "boone": ["watauga county"],
            "boonville": ["yadkin county"],
            "bostic": ["rutherford county"],
            "brevard": ["transylvania county"],
            "bridgeton": ["craven county"],
            "broadway": ["harnett county", "lee county"],
            "brookford": ["catawba county"],
            "brunswick": ["columbus county"],
            "bryson city": ["swain county"],
            "bunn": ["franklin county"],
            "burgaw": ["pender county"],
            "burlington": ["alamance county", "guilford county"],
            "burnsville": ["yancey county"],
            "butner": ["granville county"],
            "cajah's mountain": ["caldwell county"],
            "calabash": ["brunswick county"],
            "calypso": ["duplin county"],
            "cameron": ["moore county"],
            "candor": ["montgomery county"],
            "canton": ["haywood county"],
            "cape carteret": ["carteret county"],
            "carolina beach": ["new hanover county"],
            "carolina shores": ["brunswick county"],
            "carrboro": ["orange county"],
            "carthage": ["moore county"],
            "cary": ["wake county", "chatham county"],
            "casar": ["cleveland county"],
            "castalia": ["nash county"],
            "caswell beach": ["brunswick county"],
            "catawba": ["catawba county"],
            "cedar point": ["carteret county"],
            "cedar rock": ["caldwell county"],
            "centerville": ["franklin county"],
            "cerro gordo": ["columbus county"],
            "chadbourn": ["columbus county"],
            "chapel hill": ["orange county", "durham county"],
            "charlotte": ["mecklenburg county"],
            "cherryville": ["gaston county"],
            "chimney rock village": ["rutherford county"],
            "china grove": ["rowan county"],
            "chocowinity": ["beaufort county"],
            "claremont": ["catawba county"],
            "clarkton": ["bladen county"],
            "clayton": ["johnston county"],
            "clemmons": ["forsyth county"],
            "cleveland": ["rowan county"],
            "clinton": ["sampson county"],
            "clyde": ["haywood county"],
            "coats": ["harnett county"],
            "cofield": ["hertford county"],
            "colerain": ["bertie county"],
            "columbia": ["tyrrell county"],
            "columbus": ["polk county"],
            "como": ["hertford county"],
            "concord": ["cabarrus county"],
            "conetoe": ["edgecombe county"],
            "connelly springs": ["burke county"],
            "conover": ["catawba county"],
            "conway": ["northampton county"],
            "cooleemee": ["davie county"],
            "cornelius": ["mecklenburg county"],
            "cove city": ["craven county"],
            "cramerton": ["gaston county"],
            "creedmoor": ["granville county"],
            "creswell": ["washington county"],
            "crossnore": ["avery county"],
            "dallas": ["gaston county"],
            "danbury": ["stokes county"],
            "davidson": ["iredell county", "mecklenburg county"],
            "denton": ["davidson county"],
            "dillsboro": ["jackson county"],
            "dobbins heights": ["richmond county"],
            "dobson": ["surry county"],
            "dortches": ["nash county"],
            "dover": ["craven county"],
            "drexel": ["burke county"],
            "dublin": ["bladen county"],
            "duck": ["dare county"],
            "dunn": ["harnett county"],
            "durham": ["durham county"],
            "earl": ["cleveland county"],
            "east arcadia": ["bladen county"],
            "east bend": ["yadkin county"],
            "east laurinburg": ["scotland county"],
            "east spencer": ["rowan county"],
            "eastover": ["cumberland county"],
            "eden": ["rockingham county"],
            "edenton": ["chowan county"],
            "elizabeth city": ["pasquotank county", "camden county"],
            "elizabethtown": ["bladen county"],
            "elk park": ["avery county"],
            "elkin": ["surry county","wilkes county"],
            "ellenboro": ["rutherford county"],
            "ellerbe": ["richmond county"],
            "elm city": ["wilson county"],
            "elon": ["alamance county"],
            "emerald isle": ["carteret county"],
            "enfield": ["halifax county"],
            "erwin": ["harnett county"],
            "eureka": ["wayne county"],
            "everetts": ["martin county"],
            "fair bluff": ["columbus county"],
            "fairmont": ["robeson county"],
            "fairview": ["union county"],
            "faison": ["duplin county"],
            "faith": ["rowan county"],
            "falcon": ["cumberland county", "sampson county"],
            "falkland": ["pitt county"],
            "fallston": ["cleveland county"],
            "farmville": ["pitt county"],
            "fayetteville": ["cumberland county"],
            "flat rock": ["henderson county"],
            "fletcher": ["henderson county"],
            "forest city": ["rutherford county"],
            "forest hills": ["jackson county"],
            "fountain": ["pitt county"],
            "four oaks": ["johnston county"],
            "foxfire village": ["moore county"],
            "franklin": ["macon county"],
            "franklinton": ["franklin county"],
            "franklinville": ["randolph county"],
            "fremont": ["wayne county"],
            "fuquay-varina": ["wake county"],
            "gamewell": ["caldwell county"],
            "garland": ["sampson county"],
            "garner": ["wake county"],
            "garysburg": ["northampton county"],
            "gaston": ["northampton county"],
            "gastonia": ["gaston county"],
            "gatesville": ["gates county"],
            "gibson": ["scotland county"],
            "gibsonville": ["guilford county", "alamance county"],
            "glen alpine": ["burke county"],
            "godwin": ["cumberland county"],
            "goldsboro": ["wayne county"],
            "goldston": ["chatham county"],
            "graham": ["alamance county"],
            "grandfather village": ["avery county"],
            "granite falls": ["caldwell county"],
            "granite quarry": ["rowan county"],
            "grantsboro": ["pamlico county"],
            "green level": ["alamance county"],
            "greenevers": ["duplin county"],
            "greensboro": ["guilford county"],
            "greenville": ["pitt county"],
            "grifton": ["pitt county", "lenoir county"],
            "grimesland": ["pitt county"],
            "grover": ["cleveland county"],
            "halifax": ["halifax county"],
            "hamilton": ["martin county"],
            "hamlet": ["richmond county"],
            "harmony": ["iredell county"],
            "harrells": ["sampson county", "duplin county"],
            "harrellsville": ["hertford county"],
            "harrisburg": ["cabarrus county"],
            "hassell": ["martin county"],
            "havelock": ["craven county"],
            "haw river": ["alamance county"],
            "hayesville": ["clay county"],
            "hemby bridge": ["union county"],
            "henderson": ["vance county"],
            "hendersonville": ["henderson county"],
            "hertford": ["perquimans county"],
            "hickory": ["catawba county", "burke county", "caldwell county"],
            "high point": ["guilford county", "davidson county", "randolph county", "forsyth county"],
            "high shoals": ["gaston county", "lincoln county"],
            "highlands": ["macon county", "jackson county"],
            "hildebran": ["burke county"],
            "hillsborough": ["orange county"],
            "hobgood": ["halifax county"],
            "hoffman": ["richmond county"],
            "holden beach": ["brunswick county"],
            "holly ridge": ["onslow county"],
            "holly springs": ["wake county"],
            "hookerton": ["greene county"],
            "hope mills": ["cumberland county"],
            "hot springs": ["madison county"],
            "hudson": ["caldwell county"],
            "huntersville": ["mecklenburg county"],
            "indian beach": ["carteret county"],
            "indian trail": ["union county"],
            "jackson": ["northampton county"],
            "jacksonville": ["onslow county"],
            "jamestown": ["guilford county"],
            "jamesville": ["martin county"],
            "jefferson": ["ashe county"],
            "jonesville": ["yadkin county"],
            "kannapolis": ["rowan county", "cabarrus county"],
            "kelford": ["bertie county"],
            "kenansville": ["duplin county"],
            "kenly": ["johnston county", "wilson county"],
            "kernersville": ["forsyth county", "guilford county"],
            "kill devil hills": ["dare county"],
            "king": ["stokes county", "forsyth county"],
            "kings mountain": ["cleveland county", "gaston county"],
            "kingstown": ["cleveland county"],
            "kinston": ["lenoir county"],
            "kittrell": ["vance county"],
            "kitty hawk": ["dare county"],
            "knightdale": ["wake county"],
            "kure beach": ["new hanover county"],
            "la grange": ["lenoir county"],
            "lake lure": ["rutherford county"],
            "lake park": ["union county"],
            "lake santeetlah": ["graham county"],
            "lake waccamaw": ["columbus county"],
            "landis": ["rowan county"],
            "lansing": ["ashe county"],
            "lasker": ["northampton county"],
            "lattimore": ["cleveland county"],
            "laurel park": ["henderson county"],
            "laurinburg": ["scotland county"],
            "lawndale": ["cleveland county"],
            "leggett": ["edgecombe county"],
            "leland": ["brunswick county"],
            "lenoir": ["caldwell county"],
            "lewiston woodville": ["bertie county"],
            "lewisville": ["forsyth county"],
            "lexington": ["davidson county"],
            "liberty": ["randolph county"],
            "lilesville": ["anson county"],
            "lillington": ["harnett county"],
            "lincolnton": ["lincoln county"],
            "linden": ["cumberland county"],
            "littleton": ["halifax county"],
            "locust": ["stanly county", "cabarrus county"],
            "long view": ["catawba county", "burke county"],
            "louisburg": ["franklin county"],
            "love valley": ["iredell county"],
            "lowell": ["gaston county"],
            "lucama": ["wilson county"],
            "lumber bridge": ["robeson county"],
            "lumberton": ["robeson county"],
            "macclesfield": ["edgecombe county"],
            "macon": ["warren county"],
            "madison": ["rockingham county"],
            "maggie valley": ["haywood county"],
            "magnolia": ["duplin county"],
            "maiden": ["lincoln county", "catawba county"],
            "manteo": ["dare county"],
            "marietta": ["robeson county"],
            "marion": ["mcdowell county"],
            "mars hill": ["madison county"],
            "marshall": ["madison county"],
            "marshville": ["union county"],
            "marvin": ["union county"],
            "matthews": ["mecklenburg county"],
            "maxton": ["robeson county", "scotland county"],
            "mayodan": ["rockingham county"],
            "maysville": ["jones county"],
            "mcadenville": ["gaston county"],
            "mcdonald": ["robeson county"],
            "mcfarlan": ["anson county"],
            "mebane": ["alamance county", "orange county"],
            "mesic": ["pamlico county"],
            "micro": ["johnston county"],
            "middleburg": ["vance county"],
            "middlesex": ["nash county"],
            "midland": ["cabarrus county"],
            "midway": ["davidson county"],
            "mills river": ["henderson county"],
            "milton": ["caswell county"],
            "mineral springs": ["union county"],
            "minnesott beach": ["pamlico county"],
            "mint hill": ["mecklenburg county", "union county"],
            "misenheimer": ["stanly county"],
            "mocksville": ["davie county"],
            "momeyer": ["nash county"],
            "monroe": ["union county"],
            "montreat": ["buncombe county"],
            "mooresboro": ["cleveland county"],
            "mooresville": ["iredell county"],
            "morehead city": ["carteret county"],
            "morganton": ["burke county"],
            "morrisville": ["wake county", "durham county"],
            "morven": ["anson county"],
            "mount airy": ["surry county"],
            "mount gilead": ["montgomery county"],
            "mount holly": ["gaston county"],
            "mount olive": ["wayne county", "duplin county"],
            "mount pleasant": ["cabarrus county"],
            "murfreesboro": ["hertford county"],
            "murphy": ["cherokee county"],
            "nags head": ["dare county"],
            "nashville": ["nash county"],
            "navassa": ["brunswick county"],
            "new bern": ["craven county"],
            "new london": ["stanly county"],
            "newland": ["avery county"],
            "newport": ["carteret county"],
            "newton": ["catawba county"],
            "newton grove": ["sampson county"],
            "norlina": ["warren county"],
            "norman": ["richmond county"],
            "north topsail beach": ["onslow county"],
            "north wilkesboro": ["wilkes county"],
            "northwest": ["brunswick county"],
            "norwood": ["stanly county"],
            "oak city": ["martin county"],
            "oak island": ["brunswick county"],
            "oak ridge": ["guilford county"],
            "oakboro": ["stanly county"],
            "ocean isle beach": ["brunswick county"],
            "old fort": ["mcdowell county"],
            "oriental": ["pamlico county"],
            "orrum": ["robeson county"],
            "ossipee": ["alamance county"],
            "oxford": ["granville county"],
            "pantego": ["beaufort county"],
            "parkton": ["robeson county"],
            "parmele": ["martin county"],
            "patterson springs": ["cleveland county"],
            "peachland": ["anson county"],
            "peletier": ["carteret county"],
            "pembroke": ["robeson county"],
            "pikeville": ["wayne county"],
            "pilot mountain": ["surry county"],
            "pine knoll shores": ["carteret county"],
            "pine level": ["johnston county"],
            "pinebluff": ["moore county"],
            "pinehurst": ["moore county"],
            "pinetops": ["edgecombe county"],
            "pineville": ["mecklenburg county"],
            "pink hill": ["lenoir county"],
            "pittsboro": ["chatham county"],
            "pleasant garden": ["guilford county"],
            "plymouth": ["washington county"],
            "polkton": ["anson county"],
            "polkville": ["cleveland county"],
            "pollocksville": ["jones county"],
            "powellsville": ["bertie county"],
            "princeton": ["johnston county"],
            "princeville": ["edgecombe county"],
            "proctorville": ["robeson county"],
            "raeford": ["hoke county"],
            "raleigh": ["wake county"],
            "ramseur": ["randolph county"],
            "randleman": ["randolph county"],
            "ranlo": ["gaston county"],
            "raynham": ["robeson county"],
            "red cross": ["stanly county"],
            "red oak": ["nash county"],
            "red springs": ["robeson county"],
            "reidsville": ["rockingham county"],
            "rennert": ["robeson county"],
            "rhodhiss": ["burke county", "caldwell county"],
            "rich square": ["northampton county"],
            "richfield": ["stanly county"],
            "richlands": ["onslow county"],
            "river bend": ["craven county"],
            "roanoke rapids": ["halifax county"],
            "robbins": ["moore county"],
            "robbinsville": ["graham county"],
            "robersonville": ["martin county"],
            "rockingham": ["richmond county"],
            "rockwell": ["rowan county"],
            "rocky mount": ["nash county", "edgecombe county"],
            "rolesville": ["wake county"],
            "ronda": ["wilkes county"],
            "roper": ["washington county"],
            "rose hill": ["duplin county"],
            "roseboro": ["sampson county"],
            "rosman": ["transylvania county"],
            "rowland": ["robeson county"],
            "roxboro": ["person county"],
            "roxobel": ["bertie county"],
            "rural hall": ["forsyth county"],
            "ruth": ["rutherford county"],
            "rutherford college": ["burke county"],
            "rutherfordton": ["rutherford county"],
            "saint helena": ["pender county"],
            "saint james": ["brunswick county"],
            "saint pauls": ["robeson county"],
            "salemburg": ["sampson county"],
            "salisbury": ["rowan county"],
            "saluda": ["polk county", "henderson county"],
            "sandy creek": ["brunswick county"],
            "sandyfield": ["columbus county"],
            "sanford": ["lee county"],
            "saratoga": ["wilson county"],
            "sawmills": ["caldwell county"],
            "scotland neck": ["halifax county"],
            "seaboard": ["northampton county"],
            "seagrove": ["randolph county"],
            "sedalia": ["guilford county"],
            "selma": ["johnston county"],
            "seven devils": ["watauga county", "avery county"],
            "seven springs": ["wayne county"],
            "severn": ["northampton county"],
            "shallotte": ["brunswick county"],
            "sharpsburg": ["nash county", "wilson county", "edgecombe county"],
            "shelby": ["cleveland county"],
            "siler city": ["chatham county"],
            "simpson": ["pitt county"],
            "sims": ["wilson county"],
            "smithfield": ["johnston county"],
            "snow hill": ["greene county"],
            "southern pines": ["moore county"],
            "southern shores": ["dare county"],
            "southport": ["brunswick county"],
            "sparta": ["alleghany county"],
            "speed": ["edgecombe county"],
            "spencer": ["rowan county"],
            "spencer mountain": ["gaston county"],
            "spindale": ["rutherford county"],
            "spring hope": ["nash county"],
            "spring lake": ["cumberland county"],
            "spruce pine": ["mitchell county"],
            "staley": ["randolph county"],
            "stallings": ["union county"],
            "stanfield": ["cabarrus county", "stanly county"],
            "stanley": ["gaston county"],
            "stantonsburg": ["wilson county"],
            "star": ["montgomery county"],
            "statesville": ["iredell county"],
            "stedman": ["cumberland county"],
            "stem": ["granville county"],
            "stokesdale": ["guilford county"],
            "stoneville": ["rockingham county"],
            "stonewall": ["pamlico county"],
            "stovall": ["granville county"],
            "sugar mountain": ["avery county"],
            "summerfield": ["guilford county"],
            "sunset beach": ["brunswick county"],
            "surf city": ["onslow county", "pender county"],
            "swansboro": ["onslow county"],
            "swepsonville": ["alamance county"],
            "sylva": ["jackson county"],
            "tabor city": ["columbus county"],
            "tar heel": ["bladen county"],
            "tarboro": ["edgecombe county"],
            "taylorsville": ["alexander county"],
            "taylortown": ["moore county"],
            "teachey": ["duplin county"],
            "thomasville": ["davidson county"],
            "tobaccoville": ["forsyth county"],
            "topsail beach": ["pender county"],
            "trent woods": ["craven county"],
            "trenton": ["jones county"],
            "trinity": ["randolph county"],
            "troutman": ["iredell county"],
            "troy": ["montgomery county"],
            "tryon": ["polk county"],
            "turkey": ["sampson county"],
            "unionville": ["union county"],
            "valdese": ["burke county"],
            "vanceboro": ["craven county"],
            "vandemere": ["pamlico county"],
            "varnamtown": ["brunswick county"],
            "vass": ["moore county"],
            "waco": ["cleveland county"],
            "wade": ["cumberland county"],
            "wadesboro": ["anson county"],
            "wagram": ["scotland county"],
            "wake forest": ["wake county"],
            "walkertown": ["forsyth county"],
            "wallace": ["duplin county", "pender county"],
            "wallburg": ["davidson county"],
            "walnut cove": ["stokes county"],
            "walnut creek": ["wayne county"],
            "walstonburg": ["greene county"],
            "warrenton": ["warren county"],
            "warsaw": ["duplin county"],
            "washington": ["beaufort county"],
            "washington park": ["beaufort county"],
            "watha": ["pender county"],
            "waxhaw": ["union county"],
            "waynesville": ["haywood county"],
            "weaverville": ["buncombe county"],
            "webster": ["jackson county"],
            "weddington": ["mecklenburg county", "union county"],
            "weldon": ["halifax county"],
            "wendell": ["wake county"],
            "wentworth": ["rockingham county"],
            "wesley chapel": ["union county"],
            "west jefferson": ["ashe county"],
            "whispering pines": ["moore county"],
            "whitakers": ["edgecombe county", "nash county"],
            "white lake": ["bladen county"],
            "whiteville": ["columbus county"],
            "whitsett": ["guilford county"],
            "wilkesboro": ["wilkes county"],
            "williamston": ["martin county"],
            "wilmington": ["new hanover county"],
            "wilson": ["wilson county"],
            "wilson's mills": ["johnston county"],
            "windsor": ["bertie county"],
            "winfall": ["perquimans county"],
            "wingate": ["union county"],
            "winston-salem": ["forsyth county"],
            "winterville": ["pitt county"],
            "winton": ["hertford county"],
            "woodfin": ["buncombe county"],
            "woodland": ["northampton county"],
            "wrightsville beach": ["new hanover county"],
            "yadkinville": ["yadkin county"],
            "yanceyville": ["caswell county"],
            "youngsville": ["franklin county"],
            "zebulon": ["wake county"],
        };

        this.c8KeywordExpansions = {};

        // Expansions specifically for Cohort 8
        // tslint:disable:object-literal-sort-keys
        this.c9CityCountyMappings = {
            "Little Rock": ["Pulaski county"],
            "Fayetteville": ["Washington county"],
            "Fort Smith": ["Sebastian county"],
            "Springdale": ["Washington county"],
            "Jonesboro": ["Craighead county"],
            "North Little Rock": ["Pulaski county"],
            "Conway": ["Faulkner county"],
            "Rogers": ["Benton county"],
            "Bentonville": ["Benton county"],
            "Pine Bluff": ["Jefferson county"],
            "Benton": ["Saline county"],
            "Hot Springs": ["Garland county"],
            "Texarkana": ["Miller county"],
            "Sherwood": ["Pulaski county"],
            "Russellville": ["Pope county"],
            "Paragould": ["Greene county"],
            "Jacksonville": ["Pulaski county"],
            "Cabot": ["Lonoke county"],
            "Bella Vista": ["Benton county"],
            "Searcy": ["White county"],
            "Bryant": ["Saline county"],
            "West Memphis": ["Crittenden county"],
            "Van Buren": ["Crawford county"],
            "Maumelle": ["Pulaski county"],
            "El Dorado": ["Union county"],
            "Blytheville": ["Mississippi county"],
            "Forrest City": ["St. Francis county"],
            "Siloam Springs": ["Benton county"],
            "Harrison": ["Boone county"],
            "Hot Springs Village": ["Garland county"],
            "Mountain Home": ["Baxter county"],
            "Marion": ["Crittenden county"],
            "Helena-West Helena": ["Phillips county"],
            "Camden": ["Ouachita county"],
            "Magnolia": ["Columbia county"],
            "Arkadelphia": ["Clark county"],
            "Malvern": ["Hot Spring county"],
            "Batesville": ["Independence county"],
            "Hope": ["Hempstead county"],
            "Centerton": ["Benton county"],
            "Monticello": ["Drew county"],
            "Stuttgart": ["Arkansas county"],
            "Clarksville": ["Johnson county"],
            "Greenwood": ["Sebastian county"],
            "Wynne": ["Cross county"],
            "Newport": ["Jackson county"],
            "Osceola": ["Mississippi county"],
            "Lowell": ["Benton county"],
            "Beebe": ["White county"],
            "Trumann": ["Poinsett county"],
            "Heber Springs": ["Cleburne county"],
            "Morrilton": ["Conway county"],
            "Pocahontas": ["Randolph county"],
            "De Queen": ["Sevier county"],
            "Warren": ["Bradley county"],
            "Farmington": ["Washington county"],
            "Mena": ["Polk county"],
            "White Hall": ["Jefferson county"],
            "Crossett": ["Ashley county"],
            "Alma": ["Crawford county"],
            "Berryville": ["Carroll county"],
            "Walnut Ridge": ["Lawrence county"],
            "Pea Ridge": ["Benton county"],
            "Dardanelle": ["Yell county"],
            "Ashdown": ["Little River county"],
            "Dumas": ["Desha county"],
            "Greenbrier": ["Faulkner county"],
            "Cherokee Village": ["Sharp county"],
            "Barling": ["Sebastian county"],
            "Nashville": ["Howard county"],
            "Sheridan": ["Grant county"],
            "Prairie Grove": ["Washington county"],
            "Fordyce": ["Dallas county"],
            "Lonoke": ["Lonoke county"],
            "McGehee": ["Desha county"],
            "Marianna": ["Lee county"],
            "Ward": ["Lonoke county"],
            "Booneville": ["Logan county"],
            "Haskell": ["Saline county"],
            "Piggott": ["Clay county"],
            "Vilonia": ["Faulkner county"],
            "Ozark": ["Franklin county"],
            "Waldron": ["Scott county"],
            "Gosnell": ["Mississippi county"],
            "Paris": ["Logan county"],
            "Corning": ["Clay county"],
            "Johnson": ["Washington county"],
            "Manila": ["Mississippi county"],
            "Prescott": ["Nevada county"],
            "DeWitt": ["Arkansas county"],
            "Brinkley": ["Monroe county"],
            "Gentry": ["Benton county"],
            "Shannon Hills": ["Saline county"],
            "Atkins": ["Pope county"],
            "Alexander": ["Saline county"],
            "Bald Knob": ["White county"],
            "Hamburg": ["Ashley county"],
            "Pottsville": ["Pope county"],
            "England": ["Lonoke county"],
            "Hoxie": ["Lawrence county"],
            "Green Forest": ["Carroll county"],
            "Mountain View": ["Stone county"],
            "Elkins": ["Washington county"],
            "Clinton": ["Van Buren county"],
            "Little Flock": ["Benton county"],
            "Lake Village": ["Chicot county"],
            "Marked Tree": ["Poinsett county"],
            "Charleston": ["Franklin county"],
            "Tontitown": ["Washington county"],
            "Earle": ["Crittenden county"],
            "Danville": ["Yell county"],
            "Bethel Heights": ["Benton county"],
            "Huntsville": ["Madison county"],
            "Fairfield Bay": ["Van Buren county"],
            "Gravette": ["Benton county"],
            "West Fork": ["Washington county"],
            "Dermott": ["Chicot county"],
            "Lavaca": ["Sebastian county"],
            "Harrisburg": ["Poinsett county"],
            "Star City": ["Lincoln county"],
            "Eudora": ["Chicot county"],
            "Lincoln": ["Washington county"],
            "Mayflower": ["Faulkner county"],
            "Glenwood": ["Pike county"],
            "Carlisle": ["Lonoke county"],
            "Gurdon": ["Clark county"],
            "Augusta": ["Woodruff county"],
            "Horseshoe Bend": ["Izard county"],
            "Bono": ["Craighead county"],
            "Wrightsville": ["Pulaski county"],
            "Lake City": ["Craighead county"],
            "Gassville": ["Baxter county"],
            "Eureka Springs": ["Carroll county"],
            "Austin": ["Lonoke county"],
            "Judsonia": ["White county"],
            "Leachville": ["Mississippi county"],
            "Rector": ["Clay county"],
            "Bull Shoals": ["Marion county"],
            "Cave City": ["Sharp county"],
            "Lepanto": ["Poinsett county"],
            "Smackover": ["Union county"],
            "Tuckerman": ["Jackson county"],
            "Melbourne": ["Izard county"],
            "Bay": ["Craighead county"],
            "Cave Springs": ["Benton county"],
            "McCrory": ["Woodruff county"],
            "Des Arc": ["Prairie county"],
            "Decatur": ["Benton county"],
            "Stamps": ["Lafayette county"],
            "Clarendon": ["Monroe county"],
            "Mulberry": ["Crawford county"],
            "Kensett": ["White county"],
            "Brookland": ["Craighead county"],
            "Murfreesboro": ["Pike county"],
            "Salem": ["Fulton county"],
            "Lamar": ["Johnson county"],
            "Calico Rock": ["Izard county"],
            "Elm Springs": ["Washington county"],
            "Monette": ["Craighead county"],
            "Hazen": ["Prairie county"],
            "Perryville": ["Perry county"],
            "Hughes": ["St. Francis county"],
            "Cedarville": ["Crawford county"],
            "Dover": ["Pope county"],
            "Waldo": ["Columbia county"],
            "Flippin": ["Marion county"],
            "Marshall": ["Searcy county"],
            "Rison": ["Cleveland county"],
            "Hampton": ["Calhoun county"],
            "Diaz": ["Jackson county"],
            "Redfield": ["Jefferson county"],
            "Ola": ["Yell county"],
            "Lewisville": ["Lafayette county"],
            "Caraway": ["Craighead county"],
            "Greenland": ["Washington county"],
            "Mineral Springs": ["Howard county"],
            "Yellville": ["Marion county"],
            "Marvell": ["Phillips county"],
            "Luxora": ["Mississippi county"],
            "Newark": ["Independence county"],
            "Mansfield": ["Sebastian[j] county"],
            "Dierks": ["Howard county"],
            "Marmaduke": ["Greene county"],
            "Parkin": ["Cross county"],
            "Ash Flat": ["Sharp county"],
            "Mount Ida": ["Montgomery county"],
            "Goshen": ["Washington county"],
            "Highland": ["Sharp county"],
            "Horatio": ["Sevier county"],
            "London": ["Pope county"],
            "Coal Hill": ["Johnson county"],
            "Foreman": ["Little River county"],
            "Altheimer": ["Jefferson county"],
            "Mammoth Spring": ["Fulton county"],
            "Cotter": ["Baxter county"],
            "Bearden": ["Ouachita county"],
            "Kibler": ["Crawford county"],
            "East Camden": ["Ouachita county"],
            "Wilson": ["Mississippi county"],
            "Greers Ferry": ["Cleburne county"],
            "Stephens": ["Ouachita county"],
            "Oak Grove Heights": ["Greene county"],
            "Dyer": ["Crawford county"],
            "Wooster": ["Faulkner county"],
            "Fouke": ["Miller county"],
            "Magazine": ["Logan county"],
            "Norphlet": ["Union county"],
            "Gould": ["Lincoln county"],
            "Hermitage": ["Bradley county"],
            "Plumerville": ["Conway county"],
            "Hackett": ["Sebastian county"],
            "Swifton": ["Jackson county"],
            "Diamond City": ["Boone county"],
            "Oppelo": ["Conway county"],
            "Hardy": ["Sharp county"],
            "Mountain Pine": ["Garland county"],
            "Madison": ["St. Francis county"],
            "Cammack Village": ["Pulaski county"],
            "Quitman": ["Cleburne[l] county"],
            "Tyronza": ["Poinsett county"],
            "Bradford": ["White county"],
            "Keiser": ["Mississippi county"],
            "Altus": ["Franklin county"],
            "Rockport": ["Hot Spring County county"],
            "Wickes": ["Polk county"],
            "Lakeview": ["Baxter county"],
            "Lockesburg": ["Sevier county"],
            "Knoxville": ["Johnson county"],
            "Amity": ["Clark county"],
            "Weiner": ["Poinsett county"],
            "Guy": ["Faulkner county"],
            "Gillett": ["Arkansas county"],
            "McRae": ["White county"],
            "Palestine": ["St. Francis county"],
            "Imboden": ["Lawrence county"],
            "Oxford": ["Izard county"],
            "Black Rock": ["Lawrence county"],
            "Cherry Valley": ["Cross county"],
            "Cotton Plant": ["Woodruff county"],
            "Hartford": ["Sebastian county"],
            "Elaine": ["Phillips county"],
            "Caddo Valley": ["Clark county"],
            "Huntington": ["Sebastian county"],
            "Mountainburg": ["Crawford county"],
            "Bradley": ["Lafayette county"],
            "Higginson": ["White county"],
            "DeValls Bluff": ["Prairie county"],
            "Turrell": ["Crittenden county"],
            "Plainview": ["Yell county"],
            "Summit": ["Marion county"],
            "Holly Grove": ["Monroe county"],
            "Pangburn": ["White county"],
            "Huttig": ["Union county"],
            "Highfill": ["Benton county"],
            "Junction City": ["Union county"],
            "Joiner": ["Mississippi county"],
            "Bonanza": ["Sebastian county"],
            "Subiaco": ["Logan county"],
            "Taylor": ["Columbia county"],
            "Strong": ["Union county"],
            "Holland": ["Faulkner county"],
            "Humphrey": ["Arkansas[m] county"],
            "Caldwell": ["St. Francis county"],
            "Grannis": ["Polk county"],
            "Wilmot": ["Ashley county"],
            "Hartman": ["Johnson county"],
            "Emmet": ["Nevada county"],
            "Traskwood": ["Saline county"],
            "McNeil": ["Columbia county"],
            "Norfork": ["Baxter county"],
            "Sulphur Springs": ["Benton county"],
            "Wilmar": ["Drew county"],
            "Fountain Lake": ["Garland county"],
            "Central City": ["Sebastian county"],
            "Garfield": ["Benton county"],
            "Leola": ["Grant county"],
            "Calion": ["Union county"],
            "Avoca": ["Benton county"],
            "Bauxite": ["Saline county"],
            "Rose Bud": ["White county"],
            "Crawfordsville": ["Crittenden county"],
            "Ravenden": ["Lawrence county"],
            "Jasper": ["Newton county"],
            "Lafe": ["Greene county"],
            "Reyno": ["Randolph county"],
            "Sulphur Rock": ["Independence county"],
            "Bellefonte": ["Boone county"],
            "Cushman": ["Independence county"],
            "Patterson": ["Woodruff county"],
            "Hector": ["Pope county"],
            "Salesville": ["Baxter county"],
            "Grady": ["Lincoln county"],
            "Tull": ["Grant county"],
            "Kingsland": ["Cleveland county"],
            "Lake View": ["Phillips county"],
            "Belleville": ["Yell county"],
            "Leslie": ["Searcy county"],
            "Bergman": ["Boone county"],
            "Portia": ["Lawrence county"],
            "Evening Shade": ["Sharp county"],
            "Portland": ["Ashley county"],
            "Edmondson": ["Crittenden county"],
            "Sparkman": ["Dallas county"],
            "Maynard": ["Randolph county"],
            "Mount Pleasant": ["Izard county"],
            "Hatfield": ["Polk county"],
            "Dyess": ["Mississippi county"],
            "Thornton": ["Calhoun county"],
            "Gateway": ["Benton county"],
            "Alpena": ["Boone county"],
            "Winslow": ["Washington county"],
            "Midway": ["Baxter county"],
            "Grubbs": ["Jackson county"],
            "Western Grove": ["Newton county"],
            "Cove": ["Polk county"],
            "Damascus": ["Faulkner county"],
            "Colt": ["St. Francis county"],
            "Norman": ["Montgomery county"],
            "Havana": ["Yell county"],
            "Wilton": ["Little River county"],
            "Clarkedale": ["Crittenden county"],
            "Oak Grove": ["Carroll county"],
            "Emerson": ["Columbia county"],
            "Branch": ["Franklin county"],
            "Arkansas City": ["Desha county"],
            "Fredonia (Biscoe)": ["Prairie county"],
            "Mitchellville": ["Desha county"],
            "Wheatley": ["St. Francis county"],
            "Montrose": ["Ashley county"],
            "Etowah": ["Mississippi county"],
            "Pleasant Plains": ["Independence county"],
            "Biggers": ["Randolph county"],
            "Carthage": ["Dallas county"],
            "Cash": ["Craighead county"],
            "Enola": ["Faulkner county"],
            "Viola": ["Fulton county"],
            "Twin Groves": ["Faulkner county"],
            "Midland": ["Sebastian county"],
            "Bigelow": ["Perry county"],
            "Blevins": ["Hempstead county"],
            "Denning": ["Franklin county"],
            "Prattsville": ["Grant county"],
            "Menifee": ["Conway county"],
            "Strawberry": ["Lawrence county"],
            "Donaldson": ["Hot Spring county"],
            "Horseshoe Lake": ["St. Francis county"],
            "Shirley": ["Van Buren county"],
            "Poyen": ["Grant county"],
            "Lynn": ["Lawrence county"],
            "Chidester": ["Ouachita county"],
            "Knobel": ["Clay county"],
            "Lexa": ["Phillips county"],
            "Garner": ["White county"],
            "Humnoke": ["Lonoke county"],
            "Almyra": ["Arkansas county"],
            "Delight": ["Pike county"],
            "Parkdale": ["Ashley county"],
            "Buckner": ["Lafayette county"],
            "Widener": ["St. Francis county"],
            "Hickory Ridge": ["Cross county"],
            "Perrytown": ["Hempstead county"],
            "Lead Hill": ["Boone county"],
            "Perry": ["Perry county"],
            "Black Oak": ["Craighead county"],
            "Rosston": ["Nevada county"],
            "Oil Trough": ["Independence county"],
            "Keo": ["Lonoke county"],
            "Campbell Station": ["Jackson county"],
            "Letona": ["White county"],
            "Wabbaseka": ["Jefferson county"],
            "Harrell": ["Calhoun county"],
            "St. Francis": ["Clay county"],
            "Concord": ["Cleburne county"],
            "Garland": ["Miller county"],
            "Perla": ["Hot Spring county"],
            "Tollette": ["Howard county"],
            "Pineville": ["Izard county"],
            "Briarcliff": ["Baxter county"],
            "Oden": ["Montgomery county"],
            "St. Charles": ["Arkansas county"],
            "Griffithville": ["White county"],
            "Tillar": ["Drew county"],
            "Scranton": ["Logan county"],
            "Dell": ["Mississippi county"],
            "Fisher": ["Poinsett county"],
            "Pollard": ["Clay county"],
            "Pyatt": ["Marion county"],
            "Moro": ["Lee county"],
            "Russell": ["White county"],
            "Caulksville": ["Logan county"],
            "Jacksonport": ["Jackson county"],
            "Watson": ["Desha county"],
            "Adona": ["Perry county"],
            "Greenway": ["Clay county"],
            "Magness": ["Independence county"],
            "Ratcliff": ["Logan county"],
            "Fulton": ["Hempstead county"],
            "Franklin": ["Izard county"],
            "Rondo": ["Lee county"],
            "Sunset": ["Crittenden county"],
            "O'Kean": ["Randolph county"],
            "Winthrop": ["Little River county"],
            "Burdette": ["Mississippi county"],
            "Gilmore": ["Crittenden county"],
            "McDougal": ["Clay county"],
            "West Point": ["White county"],
            "Valley Springs": ["Boone county"],
            "Sidney": ["Sharp county"],
            "Ogden": ["Little River county"],
            "Tupelo": ["Jackson county"],
            "Washington": ["Hempstead county"],
            "Friendship": ["Hot Spring county"],
            "Fountain Hill": ["Ashley county"],
            "Bassett": ["Mississippi county"],
            "Fifty-Six": ["Stone county"],
            "Houston": ["Perry county"],
            "Casa": ["Perry county"],
            "Aubrey": ["Lee county"],
            "Ulm": ["Prairie county"],
            "Omaha": ["Boone county"],
            "Winchester": ["Drew county"],
            "Louann": ["Ouachita county"],
            "Anthonyville": ["Crittenden county"],
            "Gillham": ["Sevier county"],
            "Chester": ["Crawford county"],
            "Sedgwick": ["Lawrence county"],
            "Willisville": ["Nevada county"],
            "Felsenthal": ["Union county"],
            "Haynes": ["Lee county"],
            "Success": ["Clay county"],
            "Okolona": ["Clark county"],
            "Ben Lomond": ["Sevier county"],
            "Mount Vernon": ["Faulkner county"],
            "Reed": ["Desha county"],
            "Bodcaw": ["Nevada county"],
            "Moorefield": ["Independence county"],
            "Peach Orchard": ["Clay county"],
            "Everton": ["Boone county"],
            "St. Joe": ["Searcy county"],
            "Alicia": ["Lawrence county"],
            "Banks": ["Bradley county"],
            "Blue Mountain": ["Logan county"],
            "Bluff City": ["Nevada county"],
            "Georgetown": ["White county"],
            "Gum Springs": ["Clark county"],
            "Higden": ["Cleburne county"],
            "Jericho": ["Crittenden county"],
            "Ravenden Springs": ["Randolph county"],
            "Antoine": ["Pike county"],
            "Delaplaine": ["Greene county"],
            "Allport": ["Lonoke county"],
            "Daisy": ["Pike county"],
            "Jennette": ["Crittenden county"],
            "Roe": ["Monroe county"],
            "St. Paul": ["Madison county"],
            "Egypt": ["Craighead county"],
            "Pindall": ["Searcy county"],
            "Minturn": ["Lawrence county"],
            "Beedeville": ["Jackson county"],
            "Big Flat": ["Baxter county"],
            "Hunter": ["Woodruff county"],
            "Zinc": ["Boone county"],
            "South Lead Hill": ["Boone county"],
            "Beaver": ["Carroll county"],
            "Datto": ["Clay county"],
            "Black Springs": ["Montgomery county"],
            "Amagon": ["Jackson county"],
            "Fargo": ["Monroe county"],
            "Coy": ["Lonoke county"],
            "McCaskill": ["Hempstead county"],
            "Lonsdale": ["Garland county"],
            "Whelen Springs": ["Clark county"],
            "La Grange": ["Lee county"],
            "Springtown": ["Benton county"],
            "Vandervoort": ["Polk county"],
            "Guion": ["Izard county"],
            "Ozan": ["Hempstead county"],
            "Marie": ["Mississippi county"],
            "Sherrill": ["Jefferson county"],
            "Cale": ["Nevada county"],
            "Smithville": ["Lawrence county"],
            "Weldon": ["Jackson county"],
            "Williford": ["Sharp county"],
            "Powhatan": ["Lawrence county"],
            "Corinth": ["Yell county"],
            "Nimmons": ["Clay county"],
            "McNab": ["Hempstead county"],
            "Morrison Bluff": ["Logan county"],
            "Patmos": ["Hempstead county"],
            "Oakhaven": ["Hempstead county"],
            "Fourche": ["Perry county"],
            "Hindsville": ["Madison county"],
            "Rudy": ["Crawford county"],
            "Waldenburg": ["Poinsett county"],
            "Tinsman": ["Calhoun county"],
            "Birdsong": ["Mississippi county"],
            "Jerome": ["Drew county"],
            "Wiederkehr Village": ["Franklin county"],
            "Victoria": ["Mississippi county"],
            "Blue Eye": ["Carroll county"],
            "Gilbert": ["Searcy county"]
        };

        this.c9KeywordExpansions = {};

        // Expansions specifically for Cohort 8
        // tslint:disable:object-literal-sort-keys
        this.c10CityCountyMappings = {
            "Minneapolis": ["Hennepin County county"],
            "Saint Paul": ["Ramsey county"],
            "Rochester": ["Olmsted county"],
            "Duluth": ["St. Louis county"],
            "Bloomington": ["Hennepin county"],
            "Brooklyn Park": ["Hennepin county"],
            "Plymouth": ["Hennepin county"],
            "Woodbury": ["Washington county"],
            "Maple Grove": ["Hennepin county"],
            "St. Cloud": ["Stearns county", "Benton county", "Sherburne county"],
            "Lakeville": ["Dakota county"],
            "Eagan": ["Dakota county"],
            "Blaine": ["Anoka county", "Ramsey county"],
            "Eden Prairie": ["Hennepin county"],
            "Coon Rapids": ["Anoka county"],
            "Burnsville": ["Dakota county"],
            "Apple Valley": ["Dakota county"],
            "Minnetonka": ["Hennepin county"],
            "Edina": ["Hennepin county"],
            "St. Louis Park": ["Hennepin county"],
            "Moorhead": ["Clay county"],
            "Mankato": ["Blue Earth county", "Nicollet county", "Le Sueur county"],
            "Shakopee": ["Scott county"],
            "Maplewood": ["Ramsey county"],
            "Cottage Grove": ["Washington county"],
            "Roseville": ["Ramsey county"],
            "Richfield": ["Hennepin county"],
            "Inver Grove Heights": ["Dakota county"],
            "Andover": ["Anoka county"],
            "Savage": ["Scott county"],
            "Brooklyn Center": ["Hennepin county"],
            "Oakdale": ["Washington county"],
            "Fridley": ["Anoka county"],
            "Ramsey": ["Anoka county"],
            "Prior Lake": ["Scott county"],
            "Shoreview": ["Ramsey county"],
            "Chaska": ["Carver county"],
            "Winona": ["Winona county"],
            "Chanhassen": ["Carver county", "Hennepin county"],
            "White Bear Lake": ["Ramsey county", "Washington county"],
            "Owatonna": ["Steele county"],
            "Champlin": ["Hennepin county"],
            "Austin": ["Mower county"],
            "Elk River": ["Sherburne county"],
            "Rosemount": ["Dakota county"],
            "Faribault": ["Rice county"],
            "Farmington": ["Dakota county"],
            "Crystal": ["Hennepin county"],
            "Hastings": ["Dakota county", "Washington county"],
            "New Brighton": ["Ramsey county"],
            "Lino Lakes": ["Anoka county"],
            "Golden Valley": ["Hennepin county"],
            "Forest Lake": ["Washington county"],
            "New Hope": ["Hennepin county"],
            "Northfield": ["Rice county", "Dakota county"],
            "Columbia Heights": ["Anoka county"],
            "South St. Paul": ["Dakota county"],
            "West St. Paul": ["Dakota county"],
            "Willmar": ["Kandiyohi county"],
            "Stillwater": ["Washington county"],
            "Sartell": ["Stearns county", "Benton county"],
            "Hopkins": ["Hennepin county"],
            "St. Michael": ["Wright county"],
            "Otsego": ["Wright county"],
            "Buffalo": ["Wright county"],
            "Albert Lea": ["Freeborn county"],
            "Anoka": ["Anoka county"],
            "Ham Lake": ["Anoka county"],
            "Red Wing": ["Goodhue county"],
            "Hibbing": ["St. Louis county"],
            "Bemidji": ["Beltrami county"],
            "Hugo": ["Washington county"],
            "Robbinsdale": ["Hennepin county"],
            "Sauk Rapids": ["Benton county"],
            "Hutchinson": ["McLeod county"],
            "North Mankato": ["Nicollet county", "Blue Earth county"],
            "Monticello": ["Wright county"],
            "Alexandria": ["Douglas county"],
            "Fergus Falls": ["Otter Tail county"],
            "Vadnais Heights": ["Ramsey county"],
            "Rogers": ["Hennepin county"],
            "Marshall": ["Lyon county"],
            "Brainerd": ["Crow Wing county"],
            "Mounds View": ["Ramsey county"],
            "New Ulm": ["Brown county"],
            "Worthington": ["Nobles county"],
            "North St. Paul": ["Ramsey county"],
            "Waconia": ["Carver county"],
            "East Bethel": ["Anoka county"],
            "Cloquet": ["Carlton county"],
            "St. Peter": ["Nicollet county"],
            "Mendota Heights": ["Dakota county"],
            "Big Lake": ["Sherburne county"],
            "Grand Rapids": ["Itasca county"],
            "North Branch": ["Chisago county"],
            "Little Canada": ["Ramsey county"],
            "Arden Hills": ["Ramsey county"],
            "Victoria": ["Carver county"],
            "Fairmont": ["Martin county"],
            "Hermantown": ["St. Louis county"],
            "Mound": ["Hennepin county"],
            "Detroit Lakes": ["Becker county"],
            "Lake Elmo": ["Washington county"],
            "Cambridge": ["Isanti county"],
            "St. Anthony Village": ["Hennepin county", "Ramsey county"],
            "Oak Grove": ["Anoka county"],
            "Waseca": ["Waseca county"],
            "Thief River Falls": ["Pennington county"],
            "Little Falls": ["Morrison county"],
            "East Grand Forks": ["Polk county"],
            "Virginia": ["St. Louis county"],
            "Baxter": ["Crow Wing county"],
            "Orono": ["Hennepin county"],
            "Mahtomedi": ["Washington county"],
            "New Prague": ["Scott county", "Le Sueur county"],
            "Minnetrista": ["Hennepin county"],
            "Shorewood": ["Hennepin county"],
            "Wyoming": ["Chisago county"],
            "St. Francis": ["Anoka county", "Isanti county"],
            "Waite Park": ["Stearns county"],
            "Crookston": ["Polk county"],
            "Albertville": ["Wright county"],
            "St. Joseph": ["Stearns county"],
            "Belle Plaine": ["Scott county"],
            "Spring Lake Park": ["Anoka county", "Ramsey county"],
            "Medina": ["Hennepin county"],
            "Dayton": ["Hennepin county", "Wright county"],
            "Litchfield": ["Meeker county"],
            "Kasson": ["Dodge county"],
            "Jordan": ["Scott county"],
            "Delano": ["Wright county"],
            "Corcoran": ["Hennepin county"],
            "Isanti": ["Isanti county"],
            "Stewartville": ["Olmsted county"],
            "Zimmerman": ["Sherburne county"],
            "International Falls": ["Koochiching county"],
            "Byron": ["Olmsted county"],
            "Glencoe": ["McLeod county"],
            "Falcon Heights": ["Ramsey county"],
            "St. Paul Park": ["Washington county"],
            "North Oaks": ["Ramsey county"],
            "Morris": ["Stevens county"],
            "Chisago City": ["Chisago county"],
            "Lake City": ["Wabasha county", "Goodhue county"],
            "Carver": ["Carver county"],
            "Montevideo": ["Chippewa county"],
            "La Crescent": ["Houston county", "Winona county"],
            "Oak Park Heights": ["Washington county"],
            "Redwood Falls": ["Redwood county", "Renville county"],
            "Becker": ["Sherburne county"],
            "Circle Pines": ["Anoka county"],
            "Chisholm": ["St. Louis county"],
            "Elko New Market": ["Scott county"],
            "Nowthen": ["Anoka county"],
            "Princeton": ["Mille Lacs county", "Sherburne county"],
            "Lindstrom": ["Chisago county"],
            "Wayzata": ["Hennepin county"],
            "Luverne": ["Rock county"],
            "Watertown": ["Carver county"],
            "Rockford": ["Wright county", "Hennepin county"],
            "Sauk Centre": ["Stearns county"],
            "Dilworth": ["Clay county"],
            "St. James": ["Watonwan county"],
            "Windom": ["Cottonwood county"],
            "Cold Spring": ["Stearns county"],
            "Park Rapids": ["Hubbard county"],
            "Lonsdale": ["Rice county"],
            "Scandia": ["Washington county"],
            "Rice Lake": ["St. Louis county"],
            "Columbus": ["Anoka county"],
            "Grant": ["Washington county"],
            "Goodview": ["Winona county"],
            "Wadena": ["Wadena county", "Otter Tail county"],
            "Le Sueur": ["Le Sueur county", "Nicollet county", "Sibley county"],
            "Cannon Falls": ["Goodhue county"],
            "Pipestone": ["Pipestone county"],
            "Centerville": ["Anoka county"],
            "St. Augusta": ["Stearns county"],
            "Deephaven": ["Hennepin county"],
            "Independence": ["Hennepin county"],
            "Norwood Young America": ["Carver county"],
            "Bayport": ["Washington county"],
            "St. Charles": ["Winona county"],
            "Melrose": ["Stearns county"],
            "Perham": ["Otter Tail county"],
            "Newport": ["Washington county"],
            "Eveleth": ["St. Louis county"],
            "Mora": ["Kanabec county"],
            "Two Harbors": ["Lake county"],
            "Annandale": ["Wright county"],
            "Hanover": ["Wright county", "Hennepin county"],
            "Zumbrota": ["Goodhue county"],
            "Pine Island": ["Goodhue county", "Olmsted county"],
            "Sleepy Eye": ["Brown county"],
            "Ely": ["St. Louis county"],
            "Montrose": ["Wright county"],
            "Long Prairie": ["Todd county"],
            "Plainview": ["Wabasha county"],
            "Rush City": ["Chisago county"],
            "Jackson": ["Jackson county"],
            "Breckenridge": ["Wilkin county"],
            "Pine City": ["Pine county"],
            "Blue Earth": ["Faribault county"],
            "Eagle Lake": ["Blue Earth county"],
            "Montgomery": ["Le Sueur county"],
            "Benson": ["Swift county"],
            "Greenfield": ["Hennepin county"],
            "Afton": ["Washington county"],
            "Proctor": ["St. Louis county"],
            "Staples": ["Todd county", "Wadena county"],
            "Milaca": ["Mille Lacs county"],
            "Mountain Iron": ["St. Louis county"],
            "Chatfield": ["Fillmore county", "Olmsted county"],
            "Moose Lake": ["Carlton county"],
            "Dodge Center": ["Dodge county"],
            "Albany": ["Stearns county"],
            "Caledonia": ["Houston county"],
            "Cokato": ["Wright county"],
            "Osseo": ["Hennepin county"],
            "Cohasset": ["Itasca county"],
            "Granite Falls": ["Yellow Medicine county", "Chippewa county"],
            "Foley": ["Benton county"],
            "Roseau": ["Roseau county"],
            "Lexington": ["Anoka county"],
            "Sandstone": ["Pine county"],
            "Glenwood": ["Pope county"],
            "Barnesville": ["Clay county"],
            "Rockville": ["Stearns county"],
            "Pelican Rapids": ["Otter Tail county"],
            "Lauderdale": ["Ramsey county"],
            "Paynesville": ["Stearns county"],
            "Le Center": ["Le Sueur county"],
            "Lake Crystal": ["Blue Earth county"],
            "Wabasha": ["Wabasha county"],
            "Breezy Point": ["Crow Wing county"],
            "Spring Valley": ["Fillmore county"],
            "St. Bonifacius": ["Hennepin county"],
            "Crosslake": ["Crow Wing county"],
            "Excelsior": ["Hennepin county"],
            "Olivia": ["Renville county"],
            "Crosby": ["Crow Wing county"],
            "Pequot Lakes": ["Crow Wing county"],
            "Mayer": ["Carver county"],
            "Madelia": ["Watonwan county"],
            "Janesville": ["Waseca county"],
            "Winsted": ["McLeod county"],
            "Gaylord": ["Sibley county"],
            "Hawley": ["Clay county"],
            "Wells": ["Faribault county"],
            "Maple Lake": ["Wright county"],
            "Arlington": ["Sibley county"],
            "Howard Lake": ["Wright county"],
            "Maple Plain": ["Hennepin county"],
            "Nisswa": ["Crow Wing county"],
            "Tracy": ["Lyon county"],
            "Mountain Lake": ["Cottonwood county"],
            "Springfield": ["Brown county"],
            "Eyota": ["Olmsted county"],
            "Aitkin": ["Aitkin county"],
            "Slayton": ["Murray county"],
            "Coleraine": ["Itasca county"],
            "Hoyt Lakes": ["St. Louis county"],
            "Blooming Prairie": ["Steele county", "Dodge county"],
            "Hinckley": ["Pine county"],
            "Waterville": ["Le Sueur county"],
            "Lakeland": ["Washington county"],
            "Clearwater": ["Wright county", "Stearns county"],
            "Braham": ["Isanti county", "Kanabec county"],
            "Long Lake": ["Hennepin county"],
            "Kenyon": ["Goodhue county"],
            "Cologne": ["Carver county"],
            "Warroad": ["Roseau county"],
            "Spring Park": ["Hennepin county"],
            "Ortonville": ["Big Stone county"],
            "Gilbert": ["St. Louis county"],
            "Silver Bay": ["Lake county"],
            "Osakis": ["Douglas county", "Todd county"],
            "Lester Prairie": ["McLeod county"],
            "Rushford": ["Fillmore county"],
            "Mapleton": ["Blue Earth county"],
            "Canby": ["Yellow Medicine county"],
            "Rock Creek": ["Pine county"],
            "Dundas": ["Rice county"],
            "Aurora": ["St. Louis county"],
            "Lakefield": ["Jackson county"],
            "Waverly": ["Wright county"],
            "Avon": ["Stearns county"],
            "Tonka Bay": ["Hennepin county"],
            "Ada": ["Norman county"],
            "Warren": ["Marshall county"],
            "Lewiston": ["Winona county"],
            "Oronoco": ["Olmsted county"],
            "Stacy": ["Chisago county"],
            "Babbitt": ["St. Louis county"],
            "Richmond": ["Stearns county"],
            "Fosston": ["Polk county"],
            "Dassel": ["Meeker county"],
            "New London": ["Kandiyohi county"],
            "Red Lake Falls": ["Red Lake county"],
            "Bagley": ["Clearwater county"],
            "Dawson": ["Lac qui Parle county"],
            "Madison": ["Lac qui Parle county"],
            "Frazee": ["Becker county"],
            "Rice": ["Benton county"],
            "Glyndon": ["Clay county"],
            "Pierz": ["Morrison county"],
            "Grand Marais": ["Cook county"],
            "Minneota": ["Lyon county"],
            "Winthrop": ["Sibley county"],
            "Winnebago": ["Faribault county"],
            "Hayfield": ["Dodge county"],
            "Appleton": ["Swift county"],
            "Menahga": ["Wadena county"],
            "Preston": ["Fillmore county"],
            "Wheaton": ["Traverse county"],
            "Clara City": ["Chippewa county"],
            "Starbuck": ["Pope county"],
            "Spring Grove": ["Houston county"],
            "Medford": ["Steele county"],
            "Cottonwood": ["Lyon county"],
            "Royalton": ["Morrison county", "Benton county"],
            "Mahnomen": ["Mahnomen county"],
            "New York Mills": ["Otter Tail county"],
            "Spicer": ["Kandiyohi county"],
            "Adrian": ["Nobles county"],
            "Mantorville": ["Dodge county"],
            "Fulda": ["Murray county"],
            "Grand Meadow": ["Mower county"],
            "Renville": ["Renville county"],
            "Harris": ["Chisago county"],
            "New Richland": ["Waseca county"],
            "Goodhue": ["Goodhue county"],
            "Madison Lake": ["Blue Earth county"],
            "Nicollet": ["Nicollet county"],
            "Elbow Lake": ["Grant county"],
            "Fairfax": ["Renville county"],
            "Dellwood": ["Washington county"],
            "Edgerton": ["Pipestone county"],
            "Atwater": ["Kandiyohi county"],
            "Shafer": ["Chisago county"],
            "Sherburn": ["Martin county"],
            "Elgin": ["Wabasha county"],
            "Wanamingo": ["Goodhue county"],
            "Lake St. Croix Beach": ["Washington county"],
            "Tyler": ["Lincoln county"],
            "Lake Shore": ["Cass county"],
            "Hector": ["Renville county"],
            "Truman": ["Martin county"],
            "East Gull Lake": ["Cass county"],
            "Eden Valley": ["Meeker county", "Stearns county"],
            "Taylors Falls": ["Chisago county"],
            "Carlton": ["Carlton county"],
            "Baudette": ["Lake of the Woods county"],
            "Keewatin": ["Itasca county"],
            "Parkers Prairie": ["Otter Tail county"],
            "Morristown": ["Rice county"],
            "Harmony": ["Fillmore county"],
            "Buhl": ["St. Louis county"],
            "Biwabik": ["St. Louis county"],
            "Houston": ["Houston county"],
            "Bird Island": ["Renville county"],
            "Scanlon": ["Carlton county"],
            "Watkins": ["Meeker county"],
            "Nashwauk": ["Itasca county"],
            "Walker": ["Cass county"],
            "Le Roy": ["Mower county"],
            "Deer River": ["Itasca county"],
            "Henderson": ["Sibley county"],
            "Battle Lake": ["Otter Tail county"],
            "Pine River": ["Cass county"],
            "Hallock": ["Kittson county"],
            "Birchwood Village": ["Washington county"],
            "St. Stephen": ["Stearns county"],
            "Onamia": ["Mille Lacs county"],
            "Lilydale": ["Dakota county"],
            "Mazeppa": ["Wabasha county"],
            "Emily": ["Crow Wing county"],
            "Morgan": ["Redwood county"],
            "Rushford Village": ["Fillmore county"],
            "Blackduck": ["Beltrami county"],
            "St. Clair": ["Blue Earth county"],
            "Fertile": ["Polk county"],
            "Silver Lake": ["McLeod county"],
            "Henning": ["Otter Tail county"],
            "Walnut Grove": ["Redwood county"],
            "Isle": ["Mille Lacs county"],
            "Kimball": ["Stearns county"],
            "Lake Park": ["Becker county"],
            "Adams": ["Mower county"],
            "Clarkfield": ["Yellow Medicine county"],
            "Bovey": ["Itasca county"],
            "Belgrade": ["Stearns county"],
            "Dover": ["Olmsted county"],
            "Lamberton": ["Redwood county"],
            "West Concord": ["Dodge county"],
            "Elysian": ["Le Sueur county", "Waseca county"],
            "Hilltop": ["Anoka county"],
            "Cass Lake": ["Cass county"],
            "Raymond": ["Kandiyohi county"],
            "Gibbon": ["Sibley county"],
            "Twin Valley": ["Norman county"],
            "Greenwood": ["Hennepin county"],
            "Brooten": ["Stearns county", "Pope county"],
            "Mabel": ["Fillmore county"],
            "Hancock": ["Stevens county"],
            "Browerville": ["Todd county"],
            "Cleveland": ["Le Sueur county"],
            "Stockton": ["Winona county"],
            "Landfall": ["Washington county"],
            "Lanesboro": ["Fillmore county"],
            "Holdingford": ["Stearns county"],
            "Brownton": ["McLeod county"],
            "Karlstad": ["Kittson county"],
            "Marine on St. Croix": ["Washington county"],
            "Clear Lake": ["Sherburne county"],
            "Kerkhoven": ["Swift county"],
            "Westbrook": ["Cottonwood county"],
            "Trimont": ["Martin county"],
            "Greenbush": ["Roseau county"],
            "Hampton": ["Dakota county"],
            "Courtland": ["Nicollet county"],
            "Brownsdale": ["Mower county"],
            "Clarks Grove": ["Freeborn county"],
            "Buffalo Lake": ["Renville county"],
            "Hoffman": ["Grant county"],
            "Hendricks": ["Lincoln county"],
            "Ellendale": ["Steele county"],
            "Kasota": ["Le Sueur county"],
            "Marble": ["Itasca county"],
            "Freeport": ["Stearns county"],
            "Sebeka": ["Wadena county"],
            "Center City": ["Chisago county"],
            "Loretto": ["Hennepin county"],
            "La Prairie": ["Itasca county"],
            "Motley": ["Morrison county", "Cass county"],
            "Wabasso": ["Redwood county"],
            "Clarissa": ["Todd county"],
            "Hills": ["Rock county"],
            "Welcome": ["Martin county"],
            "Heron Lake": ["Jackson county"],
            "Ottertail": ["Otter Tail county"],
            "Stephen": ["Marshall county"],
            "Minnesota Lake": ["Faribault county", "Blue Earth county"],
            "Argyle": ["Marshall county"],
            "Alden": ["Freeborn county"],
            "Rollingstone": ["Winona county"],
            "Lake Benton": ["Lincoln county"],
            "Taconite": ["Itasca county"],
            "Balaton": ["Lyon county"],
            "Randall": ["Morrison county"],
            "Glenville": ["Freeborn county"],
            "Grove City": ["Meeker county"],
            "Elmore": ["Faribault county"],
            "McIntosh": ["Polk county"],
            "Jasper": ["Pipestone county", "Rock county"],
            "Barnum": ["Carlton county"],
            "Evansville": ["Douglas county"],
            "Racine": ["Mower county"],
            "Littlefork": ["Koochiching county"],
            "Butterfield": ["Watonwan county"],
            "Hill City": ["Aitkin county"],
            "Minnetonka Beach": ["Hennepin county"],
            "Verndale": ["Wadena county"],
            "Good Thunder": ["Blue Earth county"],
            "Cook": ["St. Louis county"],
            "Graceville": ["Big Stone county"],
            "Green Isle": ["Sibley county"],
            "Sabin": ["Clay county"],
            "Ironton": ["Crow Wing county"],
            "Ranier": ["Koochiching county"],
            "Halstad": ["Norman county"],
            "Lyle": ["Mower county"],
            "Hokah": ["Houston county"],
            "Sunfish Lake": ["Dakota county"],
            "Ulen": ["Clay county"],
            "Stewart": ["McLeod county"],
            "Claremont": ["Dodge county"],
            "Geneva": ["Freeborn county"],
            "Foreston": ["Mille Lacs county"],
            "Deerwood": ["Crow Wing county"],
            "Ivanhoe": ["Lincoln county"],
            "Browns Valley": ["Traverse county"],
            "Floodwood": ["St. Louis county"],
            "Clearbrook": ["Clearwater county"],
            "Audubon": ["Becker county"],
            "Eagle Bend": ["Todd county"],
            "Hamburg": ["Carver county"],
            "Pennock": ["Kandiyohi county"],
            "Willernie": ["Washington county"],
            "Amboy": ["Blue Earth county"],
            "Brownsville": ["Houston county"],
            "Sacred Heart": ["Renville county"],
            "Bethel": ["Anoka county"],
            "Gem Lake": ["Ramsey county"],
            "Prinsburg": ["Kandiyohi county"],
            "Lafayette": ["Nicollet county"],
            "Carlos": ["Douglas county"],
            "Tower": ["St. Louis county"],
            "Woodland": ["Hennepin county"],
            "Brandon": ["Douglas county"],
            "Pillager": ["Cass county"],
            "Bertha": ["Todd county"],
            "Kandiyohi": ["Kandiyohi county"],
            "Altura": ["Winona county"],
            "Erskine": ["Polk county"],
            "Rothsay": ["Wilkin"]
        };

        this.c10KeywordExpansions = {};
    }

    /**
     * For a given city name lookup the counties that city is in.
     * @param {string} cityName The name of the city the caller is interested in.
     * @param {number} iCohortId The cohort that we're interested in.
     */
    private _ConvertCityToCounty(cityName: string = "", iCohortId = 0) {
        let CityExpansion: any = {};

        switch (iCohortId) {
        case 0:
            CityExpansion = Object.assign(this.c0CityCountyMappings);
            break;

        case 1:
            CityExpansion = Object.assign(this.c1CityCountyMappings);
            break;

        case 2:
            CityExpansion = Object.assign(this.c2CityCountyMappings);
            break;

        case 4:
            CityExpansion = Object.assign(this.c4CityCountyMappings);
            break;

        case 5:
            CityExpansion = Object.assign(this.c5CityCountyMappings);
            break;

        case 6:
            CityExpansion = Object.assign(this.c5CityCountyMappings);
            break;

        case 7:
            CityExpansion = Object.assign(this.c7CityCountyMappings);
            break;

        case 8:
            CityExpansion = Object.assign(this.c7CityCountyMappings);
            break;

        case 9:
            CityExpansion = Object.assign(this.c9CityCountyMappings);
            break;

        case 10:
            CityExpansion = Object.assign(this.c10CityCountyMappings);
            break;

        default:
            CityExpansion = {};
        }

        const needle = cityName.trim().toLowerCase();

        try {
            if (needle in CityExpansion) {
                return CityExpansion[needle];
            } else {
                return [];
            }
        } catch (e) {
            console.error(`Failed to lookup related counties for city: ${needle}`, e.toString());

            return [];
        }
    }

    /**
     * Filters for valid location decorators.
     * @param {Array} locationTokensList An array of locations and sub-decorators.
     * @param {Integer} cohortID The current cohort of interest
     */
    private _PrepareLocations(locationTokensList: string[], cohortID: number = 0): any  {
        const locationTypes: any = {};

        let pieces: string[];

        locationTypes.county = [];
        locationTypes.city = [];

        for (const token of locationTokensList) {
            pieces = token.split(":").filter((el) => {
                return el.length !== 0;
            });

            if (pieces[0] === "county") {
                locationTypes[pieces[0]] = [];
            } else if (pieces[0] === "city") {
                locationTypes[pieces[0]] = [];
            }
        }

        for (const token of locationTokensList) {
            pieces = token.split(":").filter((el) => {
                return el.length !== 0;
            });

            if ((pieces.length === 2) && (Object.keys(locationTypes).includes(pieces[0]))) {
                if (pieces[0] === "city") {
                    if (!("county" in Object.keys(locationTypes))) {
                        locationTypes.county = [];
                    }

                    // Lookup the county conversion
                    const countiesExpansion = this._ConvertCityToCounty(pieces[1].toLowerCase(), cohortID);

                    // Add the city to the cities list; add the county conversion to the counties list.
                    locationTypes[pieces[0]].push(pieces[1].toLowerCase());
                    locationTypes.county = locationTypes.county.concat(countiesExpansion);
                } else {
                    locationTypes[pieces[0]].push(pieces[1].toLowerCase());
                }
            }
        }

        return locationTypes;
    }

    /**
     * From a list of query tokens, pulls out parameters that relate to database columns.
     * @param {Array} queryTokenList A list of tokens containing paramters and keywords
     */
    private _getParametersAndKeywordsFromTokens(queryTokens: string[]) {
        const parametersList = [];
        const keywordsList = [];

        for (const token of queryTokens) {
            let isKeyword = true;

            for (const decorator of this.allowedDecorators) {
                if (token.search(decorator) === 0) {
                    parametersList.push(token);
                    isKeyword = false;
                    break; // move on to the next token
                }
            }

            if (isKeyword) {
                keywordsList.push(token);
            }
        }

        return {
            parameters: parametersList,
            keywords: keywordsList,
        };
    }

    /**
     * Group parameters together (e.g. all locations in a list)
     * @param {*} parameterTokens a list of tokens that were decorated
     */
    private _groupParameters(parameterTokens: string[]) {
        const parameters: any = {};

        for (const decorator of this.allowedDecorators) {
            parameters[decorator] = [];
        }

        for (const token of parameterTokens) {
            const pieces = token.split(":").filter((el) => {
                return el.length !== 0;
            });

            if (pieces.length === 2) {
                parameters[pieces[0].toLowerCase()].push(pieces[1]);
            } else if (pieces.length === 3) {
                parameters[pieces[0].toLowerCase()].push(pieces[1].concat(":", pieces[2]));
            }
        }

        return parameters;
    }

    /**
     * Looks up other terms that should also be searched for that are related to this word/phrase.
     * @param {String} sToken A word of phrase that the user has provided.
     * @return {Array<String>} An array (or empty) of related words/phases to add to the search.
     */
    private _lookupTokenExpansions(iCohortId = 0, sToken = "") {
        sToken = (typeof sToken === "undefined") ? null : sToken;

        let SearchExpansion: any = {};

        switch (iCohortId) {
        case 0:
            SearchExpansion = Object.assign(
                this.BaseKeywordExpansions,
                this.c0KeywordExpansions,
                this.c0CityCountyMappings,
            );
            break;

        case 1:
            SearchExpansion = Object.assign(
                this.BaseKeywordExpansions,
                this.c1KeywordExpansions,
                this.c1CityCountyMappings,
            );
            break;

        case 2:
            SearchExpansion = Object.assign(
                this.BaseKeywordExpansions,
                this.c2KeywordExpansions,
                this.c2CityCountyMappings,
            );
            break;

        case 4:
            SearchExpansion = Object.assign(
                this.BaseKeywordExpansions,
                this.c4KeywordExpansions,
                this.c4CityCountyMappings,
            );
            break;

        case 5:
            SearchExpansion = Object.assign(
                this.BaseKeywordExpansions,
                this.c5KeywordExpansions,
                this.c5CityCountyMappings,
            );
            break;

        case 6:
            SearchExpansion = Object.assign(
                this.BaseKeywordExpansions,
                this.c5KeywordExpansions,
                this.c5CityCountyMappings,
            );
            break;

        case 7:
            SearchExpansion = Object.assign(
                this.BaseKeywordExpansions,
                this.c7KeywordExpansions,
                this.c7CityCountyMappings,
            );
            break;

        case 8:
            SearchExpansion = Object.assign(
                this.BaseKeywordExpansions,
                this.c8KeywordExpansions,
                this.c8CityCountyMappings,
            );
            break;

        case 9:
            SearchExpansion = Object.assign(
                this.BaseKeywordExpansions,
                this.c9KeywordExpansions,
                this.c9CityCountyMappings,
            );
            break;

        case 10:
            SearchExpansion = Object.assign(
                this.BaseKeywordExpansions,
                this.c10KeywordExpansions,
                this.c10CityCountyMappings,
            );
            break;

        default:
            SearchExpansion = Object.assign(this.BaseKeywordExpansions, {});
        }

        if (this.debug) console.log(`Searching for term expainsions for "${sToken}" in cohort: ${iCohortId}`);

        if ((sToken === null) || (sToken === "") || (sToken === undefined)) {
            return [];
        }

        const aExtraTokens = SearchExpansion[sToken.toLowerCase()];

        if (aExtraTokens === undefined) {
            return [];
        } else {
            return aExtraTokens;
        }
    }

    private _prepareAreas(areaValues: string[]) {
        const areaList = []

        for (const item of areaValues) {
            try {
                if (this.areasHash.hasOwnProperty(item.toLowerCase())) {
                    areaList.push(this.areasHash[item.toLowerCase()]);
                } else {
                    throw new Error('InvalidAreaID');
                }
            } catch (lookupErrro) {
                console.error(`Failure looking up id for area abbr: ${item}`);
            }
        }

        return areaList;
    }
}
